import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Grid, TextField, Autocomplete,
  Select, MenuItem, FormControl, InputLabel, Paper, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Alert, Chip, Accordion, AccordionSummary, AccordionDetails,
  Divider, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  FormControlLabel, Switch, createFilterOptions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  getSuppliers, getPurchaseOrders, createPurchaseOrder, updatePurchaseOrder,
  deletePurchaseOrder,
  formatCurrency, getGoodsReceipts, createGoodsReceipt, createGoodsReceiptsSplit, getShipmentsForPO,
  listPoPayments, createPoPayment, updatePoPayment, deletePoPayment,
  getBackflushPreview,
} from '../../services/procurementService';
import { getWarehouses, getInventoryBatches } from '../../services/inventoryService';
import api from '../../config/api';
import brandService from '../../services/brandService';
import { HELP } from '../inventory/sop-help-text';
import SupplierPicker from '../common/SupplierPicker';
import ConfirmDialog from '../common/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import PoDocuments from './PoDocuments';
import ComponentReadinessPanel from './ComponentReadinessPanel';
import { StatusBadge } from '../ds/StatusBadge/StatusBadge';
import { shouldAutofillPaymentTerms } from './paymentTermsAutofill';
import { skippedCostRows, skippedCostRowsWarning } from '../../utils/cogsWriteFeedback';

// Backend handlers return either { error: '...' } (custom) or
// { errors: [{msg, path}, ...] } (express-validator). Without this, the
// validator-array form falls through to axios's generic
// "Request failed with status code 400".
const extractApiError = (e, fallback = 'Request failed') => {
  const d = e?.response?.data;
  if (d?.error) return d.error;
  if (Array.isArray(d?.errors) && d.errors.length) {
    return d.errors.map(x => x.path ? `${x.msg} (${x.path})` : x.msg).join('; ');
  }
  return e?.message || fallback;
};

// PO SKU picker filter: narrow by typed text against SKU + product name, and cap
// the list so the full Products-module catalog stays responsive in the dropdown.
const skuFilter = createFilterOptions({
  stringify: (o) => `${o.sku || ''} ${o.product_name || ''}`,
  limit: 50,
});

const EMPTY_ITEM = {
  sku: '', product_name: '',
  // Customs fields per CIN7 parity — auto-fillable from product_master when SKU
  // is recognised, but operator can override per line.
  hs_code: '', country_of_origin: '',
  quantity: 1,             // qty_ordered
  received_quantity: 0,    // read-only on this form — populated by goods_receipts
  unit_price: 0,
  discount_percent: 0, tax_percent: 0, notes: '',
};

// Payment due date = supplier invoice date + days parsed from the payment terms
// (Net 30 / "14 days from invoice" → the first number; COD/Immediate/Due-on-receipt
// → 0). Mirrors the backend derivation so the form shows it the instant the
// invoice date is entered (still editable to override).
function poPaymentDueFromInvoice(invoiceDate, terms) {
  if (!invoiceDate) return '';
  const m = String(terms || '').match(/(\d+)/);
  const days = m ? parseInt(m[1], 10) : 0;
  const d = new Date(invoiceDate);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const EMPTY_FORM = {
  supplier_id: '',
  brand_id: '',
  // 'standard' | 'manufacture' (contract production — see PO Type selector).
  po_type: 'standard',
  // #2b (2026-06-18): billing entity — selected subsidiary, shown under "Bill To"
  // on the PO PDF. '' / 0 = use the company's primary subsidiary.
  subsidiary_id: '',
  po_date: new Date().toISOString().slice(0, 10),
  expected_delivery_date: '',
  currency: 'USD',
  payment_terms: 'Net 30',
  payment_due_date: '',
  shipping_address: '',
  // warehouse_code is the receipt location. Derived from selected supplier's
  // default_warehouse_code; PO inventory lands here and a separate warehouse
  // transfer moves it onward to our internal warehouses.
  warehouse_code: '',
  notes: '',
  internal_notes: '',
  description_of_goods: '',
  // Logistics & Customs
  tracking_number: '',
  carrier: '',
  transport_mode: '',
  freight_cost: 0,
  customs_duty: 0,
  bill_of_lading: '',
  incoterms: '',
  manufacturing_coo: '',
  ex_factory_date: '',
  port_of_loading: '',
  port_of_discharge: '',
  eta_port_discharge: '',
  // Supplier workflow milestones
  supplier_po_ref: '',
  po_sent_date: '',
  supplier_acceptance_date: '',
  origin_port_etd: '',
  freight_booking_confirmed_date: '',
  supplier_invoice_number: '',
  supplier_invoice_date: '',
  supplier_invoice_total: 0,
  // T&Cs (Tier 1: PO-level override; takes precedence over supplier + subsidiary defaults
  // resolved at PDF render time). suppress_terms = render no T&Cs section at all on this PO.
  terms_and_conditions: '',
  suppress_terms: 0,
  // Per-PO supplier-detail overrides (Phase 1 of ad-hoc-override pattern).
  // Empty = "use the master supplier record's value". Non-empty = "use this
  // for THIS PO only" — PDF + UI both layer override-on-top in the same way.
  supplier_contact_name_override: '',
  supplier_contact_email_override: '',
  supplier_contact_phone_override: '',
  supplier_address_line1_override: '',
  supplier_address_line2_override: '',
  supplier_city_override: '',
  supplier_state_override: '',
  supplier_country_override: '',
  supplier_postal_code_override: '',
  items: [{ ...EMPTY_ITEM }],
};

// Download this PO's printable PDF (same endpoint the list view uses). Mirrors
// openPoPdf() in PurchaseOrders.js but uses the relative api base.
async function downloadPoPdf(id, poNumber, onError) {
  try {
    const resp = await api.get(`/api/procurement/purchase-orders/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
    const a = document.createElement('a'); a.href = url;
    a.download = `PurchaseOrder-${poNumber || id}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    onError?.(e.response?.data?.error || e.message);
  }
}

export default function PurchaseOrderEntry() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  // /procurement/purchase-orders/:id/edit OR ?edit=N
  const editId = routeId && routeId !== 'new' ? routeId : (searchParams.get('edit') || null);
  const isEdit = !!editId;
  // Return to the FILTERED list the operator came from — the list stashes its
  // filter query-string in sessionStorage (Rishabh 2026-07-02: Back/Cancel/Save
  // must land on the filtered list, not the whole unfiltered list).
  const listBackTo = `/procurement/purchase-orders${(() => { try { return sessionStorage.getItem('scm.poListSearch') || ''; } catch (_) { return ''; } })()}`;

  // Shared MUI confirm — same primitive as PurchaseOrders/Suppliers, replacing
  // the native window.confirm popups on the payment/receipt flows below.
  const { confirmDialogProps, confirm } = useConfirmDialog();

  // "Has the operator edited anything since load/save?" — drives the
  // unsaved-changes guard on Back/Cancel.
  //
  // Deliberately NOT a deep compare of form-vs-snapshot: the supplier effect
  // below auto-fills currency / warehouse_code / payment_terms moments after
  // load, so a snapshot taken at load time reads dirty immediately and the
  // guard would nag on every single exit.
  // Instead dirtiness is marked at the WRITE: setForm() marks, setFormSilent()
  // does not, so the four programmatic writers (load, supplier autofill, server
  // refresh, product-name backfill) are ignored while every operator edit
  // counts. onChangeCapture on the wrapper stays as a backstop for inputs that
  // are not routed through setForm.
  const [userTouched, setUserTouched] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [unreceiving, setUnreceiving] = useState(false);
  const [unreceiveConfirmOpen, setUnreceiveConfirmOpen] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Non-blocking: the receipt was recorded but the server skipped some product cost rows (cost_rows_skipped, W20).
  const [warning, setWarning] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]); // #2b: billing-entity options
  const [warehouses, setWarehouses] = useState([]);
  const [form, setFormRaw] = useState(EMPTY_FORM);
  // setFormSilent = write the form WITHOUT marking it dirty. Only for
  // programmatic writes (initial load, the supplier autofill effect, the
  // server refresh, the product-name backfill) — those are not operator edits.
  const setFormSilent = setFormRaw;
  const [paymentTermsManuallyEdited, setPaymentTermsManuallyEdited] = useState(false);
  // Supplier defaults (currency, receipt warehouse, payment terms) belong to
  // the moment the OPERATOR picks a supplier. On an edit load the supplier id
  // arrives from the saved PO, and applying the defaults then silently
  // replaced the PO's own currency and warehouse with the supplier's: PO 396
  // (CHF; Cosmotec's default is USD) rendered as US$, and once an operator
  // saved it, it WAS restated to USD (audit_log 2026-09-08; the payment
  // equivalents were healed by job #421). true only once the picker fires.
  const [supplierPickedByOperator, setSupplierPickedByOperator] = useState(false);
  const [lastSupplierPaymentTerms, setLastSupplierPaymentTerms] = useState('');
  const [poNumber, setPoNumber] = useState(''); // for the Download-PDF filename
  const [poClosedNotice, setPoClosedNotice] = useState(null); // banner when editing a closed/done PO
  const [knownSkus, setKnownSkus] = useState([]);
  const [catalogSkus, setCatalogSkus] = useState([]);
  // Cache stock-per-warehouse for each SKU seen on the form — avoids refetching
  // when the operator re-types the same SKU. Shape: { 'SS00001': { total: 1243,
  // per_wh: [{warehouse_code, available}] } }
  const [stockBySku, setStockBySku] = useState({});
  // Receipts already recorded against this PO (edit mode only). Used by the
  // Receipts panel + "Record Receipt" flow for partial fulfilment.
  const [receipts, setReceipts] = useState([]);
  // Signature of the PO lines as last persisted server-side. A goods receipt
  // is recorded against the SAVED PO (the backend resolves qty caps and unit
  // cost from purchase_order_items) — so "Record Receipt" must not proceed
  // while on-screen line edits are unsaved: the receipt would silently use
  // the stale qty/cost and the post-receipt refresh would then clobber the
  // operator's typed values (Rishabh, PO #361, 2026-07-08). Tracks the fields
  // the backend reads from the DB at receipt time.
  const savedLineSigRef = useRef('');
  // Covers every line field the post-receipt refresh would clobber, plus the
  // PO currency (the backend FX-converts the batch cost from the PERSISTED
  // currency at receipt time — an unsaved currency change would misprice it).
  const lineSig = (items, currency) => (currency || '') + '::' + (items || [])
    .filter(i => i.sku && Number(i.quantity) > 0)
    .map(i => [
      String(i.sku).toLowerCase(), Number(i.quantity) || 0, Number(i.unit_price) || 0,
      Number(i.discount_percent) || 0, Number(i.tax_percent) || 0,
      i.product_name || '', i.hs_code || '', i.country_of_origin || '', i.notes || '',
    ].join('|'))
    .sort()
    .join('||');
  // Map a server PO payload's items into the form's line shape. Shared by the
  // initial load, the post-receipt refresh and the save-before-receipt path so
  // po_item_ids and received quantities always come from the same mapping.
  const mapServerItems = (po) => (po.items || []).map(i => ({
    po_item_id: Number(i.id || 0),
    sku: i.sku || '',
    product_name: i.product_name || i.description || '',
    hs_code: i.hs_code || '',
    country_of_origin: i.country_of_origin || '',
    quantity: Number(i.ordered_quantity ?? i.quantity ?? 0),
    received_quantity: Number(i.received_quantity || 0),
    unit_price: Number(i.unit_cost ?? i.unit_price ?? 0),
    discount_percent: Number(i.discount_percent || 0),
    tax_percent: Number(i.tax_percent || 0),
    notes: i.notes || '',
  }));
  // Re-pull the PO's lines from the server (fresh po_item_ids for new lines +
  // canonical received quantities) and sync the form + saved-lines signature.
  const refreshPoItemsFromServer = async () => {
    const single = await api.get(`/api/procurement/purchase-orders/${editId}`);
    const po = single?.data?.data || {};
    const mapped = mapServerItems(po);
    setFormSilent(prev => ({ ...prev, items: mapped }));
    savedLineSigRef.current = lineSig(mapped, po.currency || 'USD');
    return mapped;
  };
  const [payments, setPayments] = useState([]);
  // The inline Add Payment form is its own transaction as well: its Add button
  // persists the row immediately (on a closed PO too; payments never needed
  // the PO to be open). Typing in it was still marking the PO form dirty via
  // the page-level onChangeCapture, so after Add the Back arrow warned
  // "Discard your changes?" about a payment already saved, and the operator
  // read the discard as having saved it (Rishabh 2026-09-04). The form is
  // always rendered, so unlike the two dialogs there is no open/closed state
  // to filter on; its subtree is excluded by containment instead.
  const addPaymentRef = useRef(null);
  const [newPayment, setNewPayment] = useState({ payment_date: new Date().toISOString().slice(0, 10), amount: '', currency: '', amount_in_po_currency: '', method: 'bank_transfer', reference: '', invoice_number: '', notes: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  // Auto-fill the PO-currency equivalent from the day's FX rate (editable — the
  // operator overrides with the exact figure their bank converted). Multi-currency
  // PO payments, Rishabh 2026-07-02.
  const fetchEquivalent = async (amt, fromCcy) => {
    const a = parseFloat(amt);
    if (!a || !fromCcy || fromCcy === form.currency) return;
    try {
      const r = await api.get('/api/accounting/exchange-rates/convert', { params: { amount: a, from: fromCcy, to: form.currency } });
      const conv = r?.data?.data?.convertedAmount ?? r?.data?.convertedAmount;
      if (conv != null) setNewPayment((prev) => ({ ...prev, amount_in_po_currency: String(Math.round(Number(conv) * 100) / 100) }));
    } catch (_) { /* leave blank for manual entry */ }
  };

  const loadPayments = async () => {
    if (!editId) return;
    try {
      const r = await listPoPayments(editId);
      setPayments(r?.data || []);
    } catch (_) { setPayments([]); }
  };
  const addPayment = async () => {
    if (!newPayment.amount || Number(newPayment.amount) <= 0) { setError('Payment amount must be > 0'); return; }
    const payCcy = newPayment.currency || form.currency;
    const diffCcy = payCcy !== form.currency;
    if (diffCcy && (!newPayment.amount_in_po_currency || Number(newPayment.amount_in_po_currency) <= 0)) {
      setError(`Enter what this payment covers in ${form.currency} (the PO currency)`); return;
    }
    setSavingPayment(true);
    try {
      await createPoPayment(editId, {
        payment_date: newPayment.payment_date,
        amount: parseFloat(newPayment.amount),
        currency: payCcy,
        amount_in_po_currency: diffCcy ? parseFloat(newPayment.amount_in_po_currency) : parseFloat(newPayment.amount),
        method: newPayment.method,
        reference: newPayment.reference,
        invoice_number: newPayment.invoice_number,
        notes: newPayment.notes,
      });
      setNewPayment({ payment_date: new Date().toISOString().slice(0, 10), amount: '', currency: '', amount_in_po_currency: '', method: 'bank_transfer', reference: '', invoice_number: '', notes: '' });
      await loadPayments();
    } catch (e) {
      setError(extractApiError(e, 'Failed to add payment'));
    } finally { setSavingPayment(false); }
  };
  const removePayment = async (paymentId) => {
    const ok = await confirm({
      title: 'Delete this payment?',
      message: 'The payment record is removed from this PO and the amount paid is recalculated.',
      confirmText: 'Delete payment',
      severity: 'danger',
    });
    if (!ok) return;
    try {
      await deletePoPayment(editId, paymentId);
      await loadPayments();
    } catch (e) {
      setError(extractApiError(e, 'Failed to delete payment'));
    }
  };
  // Inline edit. Operator was previously stuck doing delete+re-add to fix a
  // typo'd reference or wrong date. editingPayment=null means dialog closed.
  const [editingPayment, setEditingPayment] = useState(null);
  const openEditPayment = (p) => {
    setError(null); setEditingPayment({
      id: p.id,
      payment_date: (p.payment_date || '').slice(0, 10),
      amount: String(p.amount ?? ''),
      currency: p.currency || form.currency,
      amount_in_po_currency: String(p.amount_in_po_currency ?? ''),
      method: p.method || 'bank_transfer',
      reference: p.reference || '',
      notes: p.notes || '',
    });
  };
  const saveEditPayment = async () => {
    if (!editingPayment) return;
    if (!editingPayment.amount || Number(editingPayment.amount) <= 0) {
      setError('Payment amount must be > 0');
      return;
    }
    setSavingPayment(true);
    try {
      await updatePoPayment(editId, editingPayment.id, {
        payment_date: editingPayment.payment_date,
        amount: parseFloat(editingPayment.amount),
        // Currency is immutable server-side; send the (possibly edited) PO-currency
        // equivalent so a cross-currency payment's settled value stays right.
        amount_in_po_currency: (editingPayment.currency && editingPayment.currency !== form.currency && editingPayment.amount_in_po_currency !== '')
          ? parseFloat(editingPayment.amount_in_po_currency) : undefined,
        method: editingPayment.method,
        reference: editingPayment.reference,
        invoice_number: editingPayment.invoice_number || '',
        notes: editingPayment.notes,
      });
      setEditingPayment(null);
      await loadPayments();
    } catch (e) {
      setError(extractApiError(e, 'Failed to update payment'));
    } finally { setSavingPayment(false); }
  };
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  // Edits made inside the Record Receipt / Edit Payment dialogs belong to THEIR
  // own transactions, not to the PO form: a goods receipt persists the moment
  // it is recorded and Back cannot undo it. Without this exclusion, typing a
  // receipt quantity marked the PO form dirty, recording the receipt saved it
  // independently (never clearing the flag), and Back then warned "Discard your
  // changes?" about data that was already safely saved. A guard that cries wolf
  // on saved data trains the operator to click through it, which defeats it on
  // the one case that matters — genuine unsaved PO edits.
  // (Note: this must be filtered here, not with stopPropagation in the dialog —
  // onChangeCapture runs parent-first, so the wrapper fires before any child.)
  const suppressDirty = receiptDialogOpen || !!editingPayment;
  const markTouched = useCallback(() => {
    if (suppressDirty) return;
    setUserTouched(true);
  }, [suppressDirty]);

  // Every operator-driven write to the form goes through here, so the dirty flag
  // does not depend on a DOM event being emitted. That matters because MUI's
  // Select and Autocomplete call their onChange PROP directly and never dispatch
  // a change event into the DOM — so the onChangeCapture on the wrapper below
  // sees typing but is blind to every dropdown. Marking at the setter covers
  // both, and any control added later, without touching 42 call sites.
  const setForm = useCallback((updater) => {
    markTouched();
    setFormRaw(updater);
  }, [markTouched]);
  // DOM backstop for inputs not routed through setForm; skips the inline Add
  // Payment form (see addPaymentRef above).
  const markTouchedFromDom = useCallback((e) => {
    if (addPaymentRef.current && addPaymentRef.current.contains(e.target)) return;
    markTouched();
  }, [markTouched]);

  // Wrap any control that leaves the page; prompts only when there are edits.
  const guardedLeave = useUnsavedChangesGuard(userTouched, confirm);
  const [receiptForm, setReceiptForm] = useState({
    receipt_date: new Date().toISOString().slice(0, 10),
    notes: '',
    // Destination warehouse for this whole receipt. Defaults from PO.warehouse_code
    // when the dialog opens; operator can override. Per-line splits across
    // multiple destinations (Pattern B) are a deliberate follow-up that needs
    // a schema + stock-levels CTE change.
    warehouse_code: '',
    items: [], // [{ po_item_id, sku, qty_to_receive, qty_to_accept }]
  });
  const [recordingReceipt, setRecordingReceipt] = useState(false);
  // Manufacture POs: per-line backflush preview state, keyed by po_item_id —
  // { loading, error, warnings, components: [{component_sku, quantity, ...}],
  //   stale } — `stale` flips on when the accepted qty changes after loading
  // so the operator recalculates before submitting.
  const isManufacturePo = form.po_type === 'manufacture';
  const [backflushByLine, setBackflushByLine] = useState({});
  const [confirmShortfall, setConfirmShortfall] = useState(false);

  const loadBackflushPreview = async (poItemId, quantity, receiptDate) => {
    if (!editId || !(quantity > 0)) {
      setBackflushByLine(prev => ({ ...prev, [poItemId]: { loading: false, components: [], warnings: [], error: null, stale: false } }));
      return;
    }
    setBackflushByLine(prev => ({ ...prev, [poItemId]: { ...(prev[poItemId] || {}), loading: true, error: null, stale: false } }));
    try {
      const data = await getBackflushPreview(editId, poItemId, quantity, receiptDate);
      setBackflushByLine(prev => ({
        ...prev,
        [poItemId]: {
          loading: false, error: null, stale: false,
          // The qty this plan was computed for. A response that lands after the
          // operator already changed the qty must not count as fresh.
          computed_for: quantity,
          warnings: data.warnings || [],
          components: (data.components || []).map(c => ({ ...c, quantity: c.quantity })),
          // Bulk-fill lines: the receipt builds the assembly, not the line SKU.
          is_bulkfill: !!data.is_bulkfill,
          line_sku: data.line_sku || '',
          produces_sku: data.produces_sku || '',
          produces_quantity: data.produces_quantity,
        },
      }));
    } catch (e) {
      setBackflushByLine(prev => ({
        ...prev,
        [poItemId]: {
          loading: false, stale: false, components: [], warnings: [],
          error: e.response?.data?.error || e.message || 'Failed to compute components',
        },
      }));
    }
  };

  // Recalculate a line's component plan automatically (debounced) once its
  // received qty differs from the qty the plan was computed for. Before this the
  // operator had to spot a Recalculate button, and a preview that landed after
  // they had already typed a new qty showed as fresh (Rishabh 2026-10-13: typed
  // 3,900 but the plan still said 10,000).
  const receiveQtyKey = isManufacturePo && receiptDialogOpen
    // computed_for/loading are in the key so a plan that lands for an older qty
    // (operator typed while it loaded) triggers one more recalculation.
    ? receiptForm.items.map(r => {
      const bf = backflushByLine[r.po_item_id];
      return `${r.po_item_id}:${r.qty_to_receive}:${bf ? `${bf.computed_for}:${bf.loading ? 1 : 0}` : '-'}`;
    }).join('|')
    : '';
  useEffect(() => {
    if (!receiveQtyKey) return undefined;
    const timer = setTimeout(() => {
      for (const r of receiptForm.items) {
        const bf = backflushByLine[r.po_item_id];
        // Skip a line whose last load failed: retrying here would loop on the
        // error; the operator retries with the Recalculate button.
        if (r.qty_to_receive > 0 && bf && !bf.loading && !bf.error && bf.computed_for !== r.qty_to_receive) {
          loadBackflushPreview(r.po_item_id, r.qty_to_receive, receiptForm.receipt_date);
        }
      }
    }, 600);
    return () => clearTimeout(timer);
    // Keyed on the qtys only: re-running on every plan/state change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiveQtyKey]);

  const setBackflushComponentQty = (poItemId, componentSku, value) => {
    setBackflushByLine(prev => {
      const line = prev[poItemId];
      if (!line) return prev;
      return {
        ...prev,
        [poItemId]: {
          ...line,
          components: line.components.map(c =>
            c.component_sku === componentSku ? { ...c, quantity: Math.max(0, Number(value) || 0) } : c
          ),
        },
      };
    });
  };
  // Two-stage flow: list of dispatched shipments for this PO. When recording a
  // GR, if exactly one shipment is in 'shipped' or 'arrived' state with capacity
  // remaining, we auto-attach the GR to it (passes shipment_id in payload →
  // backend posts the reversing JE Dr 1200 / Cr 1220). Multi-shipment case is a
  // future picker; today operator can still record a GR (with no shipment_id),
  // and the cin7-ap-processor handles the inventory side as before.
  const [dispatchedShipments, setDispatchedShipments] = useState([]);

  const loadReceipts = async () => {
    if (!editId) return;
    try {
      const r = await getGoodsReceipts({ po_id: editId, limit: 100 });
      const list = r?.data?.goods_receipts || r?.goods_receipts || r?.data || [];
      setReceipts(Array.isArray(list) ? list : []);
    } catch (_) { setReceipts([]); }
  };
  const loadDispatchedShipments = async () => {
    if (!editId) return;
    try {
      const r = await getShipmentsForPO(editId);
      const list = r?.data || r?.shipments || [];
      const dispatched = (Array.isArray(list) ? list : []).filter(s =>
        s.is_active !== 0 && (s.status === 'shipped' || s.status === 'arrived')
      );
      setDispatchedShipments(dispatched);
    } catch (_) { setDispatchedShipments([]); }
  };

  // Initial load: suppliers + brands + existing PO (if edit).
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [sRes, bRes, wRes, subRes] = await Promise.all([
          // include_intercompany=true so PO entry sees IC vendors too — the
          // suppliers service hides them by default (see suppliers/service.js).
          getSuppliers({ is_active: true, include_intercompany: true, limit: 1000 }),
          brandService.getBrands(),
          getWarehouses({ is_active: true, exclude_virtual: true }),
          api.get('/api/subsidiaries').catch(() => ({ data: [] })), // #2b billing entities
        ]);
        const sList = Array.isArray(sRes?.data) ? sRes.data : (sRes?.data?.suppliers || []);
        setSuppliers((sList || []).filter(s => s.supplier_name));
        const bList = Array.isArray(bRes) ? bRes : (bRes?.data || bRes?.brands || []);
        setBrands((bList || []).filter(b => b.id !== 1 && b.database));
        const wList = wRes?.data || wRes?.warehouses || [];
        setWarehouses(Array.isArray(wList) ? wList : []);
        // #2b: billing-entity options for the "Bill To" selector.
        const subList = subRes?.data?.data || subRes?.data?.subsidiaries || subRes?.data || [];
        setSubsidiaries(Array.isArray(subList) ? subList.filter(x => x && x.id) : []);
        if (isEdit) {
          const single = await api.get(`/api/procurement/purchase-orders/${editId}`);
          const po = single?.data?.data || {};
          setPoNumber(po.po_number || '');
          // A closed/done PO OPENS for viewing but is READ-ONLY — edits can't be saved
          // until it's Reopened. (Rishabh 2026-07-01: closed edits should not save;
          // only a reopened PO accepts changes.) poClosedNotice truthy = view-only.
          {
            // Reopen truly unlocks (Rishabh 2026-07-02): the ONLY lock is the
            // Closed flag. A fully-received-but-not-closed PO — and any reopened
            // PO (is_closed=0) — is editable. Previously 'received'/'completed'
            // were force-locked even after reopen, so Reopen appeared to "do
            // nothing." Payments were always editable regardless (separate path).
            const st = String(po.status || '').toLowerCase();
            const done = po.is_closed === 1 || po.is_closed === true || st === 'closed';
            setPoClosedNotice(done
              ? `This PO is closed and is read-only. Click Reopen to make and save changes.`
              : null);
          }
          setPaymentTermsManuallyEdited(true);
          setFormSilent({
            ...EMPTY_FORM,
            supplier_id: po.supplier_id || '',
            brand_id: po.brand_id || '',
            po_type: po.po_type || 'standard',
            subsidiary_id: po.subsidiary_id || '', // #2b
            po_date: (po.po_date || '').slice(0, 10) || EMPTY_FORM.po_date,
            expected_delivery_date: (po.expected_delivery_date || '').slice(0, 10) || '',
            currency: po.currency || 'USD',
            payment_terms: po.payment_terms || 'Net 30',
            payment_due_date: (po.payment_due_date || '').slice(0, 10) || '',
            shipping_address: po.shipping_address || '',
            warehouse_code: po.warehouse_code || '',
            notes: po.notes || '',
            internal_notes: po.internal_notes || '',
            description_of_goods: po.description_of_goods || '',
            tracking_number: po.tracking_number || '',
            carrier: po.carrier || '',
            transport_mode: po.transport_mode || '',
            freight_cost: Number(po.freight_cost || po.shipping_cost || 0),
            customs_duty: Number(po.customs_duty || 0),
            bill_of_lading: po.bill_of_lading || '',
            incoterms: po.incoterms || '',
            manufacturing_coo: po.manufacturing_coo || '',
            ex_factory_date: (po.ex_factory_date || '').slice(0, 10) || '',
            port_of_loading: po.port_of_loading || '',
            port_of_discharge: po.port_of_discharge || '',
            eta_port_discharge: (po.eta_port_discharge || '').slice(0, 10) || '',
            supplier_po_ref: po.supplier_po_ref || '',
            po_sent_date: (po.po_sent_date || '').slice(0, 10) || '',
            supplier_acceptance_date: (po.supplier_acceptance_date || '').slice(0, 10) || '',
            origin_port_etd: (po.origin_port_etd || '').slice(0, 10) || '',
            freight_booking_confirmed_date: (po.freight_booking_confirmed_date || '').slice(0, 10) || '',
            supplier_invoice_number: po.supplier_invoice_number || '',
            supplier_invoice_date: (po.supplier_invoice_date || '').slice(0, 10) || '',
            supplier_invoice_total: po.supplier_invoice_total || 0,
            terms_and_conditions: po.terms_and_conditions || '',
            suppress_terms: Number(po.suppress_terms || 0),
            supplier_contact_name_override: po.supplier_contact_name_override || '',
            supplier_contact_email_override: po.supplier_contact_email_override || '',
            supplier_contact_phone_override: po.supplier_contact_phone_override || '',
            supplier_address_line1_override: po.supplier_address_line1_override || '',
            supplier_address_line2_override: po.supplier_address_line2_override || '',
            supplier_city_override: po.supplier_city_override || '',
            supplier_state_override: po.supplier_state_override || '',
            supplier_country_override: po.supplier_country_override || '',
            supplier_postal_code_override: po.supplier_postal_code_override || '',
            items: (po.items && po.items.length) ? mapServerItems(po) : [{ ...EMPTY_ITEM }],
          });
          // Baseline for the unsaved-line-edits guard on "Record Receipt".
          savedLineSigRef.current = lineSig(mapServerItems(po), po.currency || 'USD');
          // Receipts already recorded against this PO. Used to render the
          // Receipts panel and to enable "Record Receipt".
          loadReceipts();
          loadPayments();
          loadDispatchedShipments();
        }
      } catch (e) {
        setError(extractApiError(e, 'Failed to load form data'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // When supplier picked, fetch their previously-ordered SKUs AND auto-set
  // currency + receipt warehouse from the supplier defaults.
  useEffect(() => {
    if (!form.supplier_id) { setKnownSkus([]); return; }
    (async () => {
      try {
        const r = await api.get(`/api/procurement/suppliers/${form.supplier_id}/known-skus`);
        setKnownSkus(r.data?.data || []);
      } catch (_) { setKnownSkus([]); }
    })();
    const s = suppliers.find(x => x.id === form.supplier_id);
    // On an edit load keep the PO's own values; apply the supplier's defaults
    // only after the operator picks a supplier (see supplierPickedByOperator).
    if (s && (!isEdit || supplierPickedByOperator)) {
      // Pre-fill warehouse_code only when the supplier's default appears in
      // the picker. The picker drops 'git' / 'legacy' (exclude_virtual=true);
      // manufacturer + supplier types ARE listed because direct-ship to a
      // factory is a legitimate destination. If the default isn't in the
      // picker (e.g. it's GIT or stale), leave the field blank rather than
      // silently submitting a value the operator can't see.
      const defaultCode = s.default_warehouse_code;
      const defaultInPicker = defaultCode && warehouses.some(w => w.code === defaultCode);
      setFormSilent(prev => ({
        ...prev,
        currency: (s.currency && prev.currency !== s.currency) ? s.currency : prev.currency,
        warehouse_code: defaultInPicker ? defaultCode : (defaultCode ? '' : prev.warehouse_code),
        payment_terms: shouldAutofillPaymentTerms({
          currentTerms: prev.payment_terms,
          previousSupplierTerms: lastSupplierPaymentTerms,
          supplierTerms: s.payment_terms,
          manuallyEdited: paymentTermsManuallyEdited,
        }) ? s.payment_terms : prev.payment_terms,
      }));
    }
    if (s) setLastSupplierPaymentTerms(s.payment_terms || '');
    // warehouses.length in deps so we re-evaluate when the warehouses list
    // arrives after supplier was already picked (otherwise a virtual default
    // would stick because the physical-check ran against an empty list).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.supplier_id, suppliers.length, warehouses.length, paymentTermsManuallyEdited, lastSupplierPaymentTerms, supplierPickedByOperator]);

  // Full brand catalog (active product_master SKUs) so the operator can add a
  // SKU that was never ordered from this supplier before — the supplier-scoped
  // known-skus list omits those. Brand-scoped; cleared when no brand is chosen.
  useEffect(() => {
    if (!form.brand_id) { setCatalogSkus([]); return; }
    (async () => {
      try {
        const r = await api.get(`/api/procurement/brands/${parseInt(form.brand_id)}/catalog-skus`);
        setCatalogSkus((r.data?.data || []).map(p => ({ sku: p.sku, product_name: p.product_name, stock_uom: p.stock_uom })));
      } catch (_) { setCatalogSkus([]); }
    })();
  }, [form.brand_id]);

  const selectedSupplier = useMemo(() =>
    suppliers.find(s => s.id === form.supplier_id), [suppliers, form.supplier_id]);

  // line_total formula: qty × price × (1 − disc%) × (1 + tax%) — matches backend.
  const computeLineTotal = (it) => {
    const q = Number(it.quantity) || 0;
    const p = Number(it.unit_price) || 0;
    const d = Number(it.discount_percent) || 0;
    const t = Number(it.tax_percent) || 0;
    return q * p * (1 - d / 100) * (1 + t / 100);
  };
  const totals = useMemo(() => {
    let qty = 0, gross = 0, discount = 0, tax = 0, total = 0;
    form.items.forEach(it => {
      const q = Number(it.quantity) || 0;
      const p = Number(it.unit_price) || 0;
      const d = Number(it.discount_percent) || 0;
      const tx = Number(it.tax_percent) || 0;
      const g = q * p;
      const da = g * (d / 100);
      const net = g - da;
      const ta = net * (tx / 100);
      qty += q;
      gross += g;
      discount += da;
      tax += ta;
      total += net + ta;
    });
    return { qty, gross, discount, tax, total };
  }, [form.items]);

  const updateItem = (idx, field, value) => {
    const next = [...form.items];
    next[idx] = { ...next[idx], [field]: value };
    setForm({ ...form, items: next });
  };
  const addItem = () => { setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] }); };
  const removeItem = (idx) => { setForm({ ...form, items: form.items.filter((_, i) => i !== idx) }); };

  // Fetch a single FX rate via the procurement helper endpoint. Returns 1 on
  // any error so the form remains usable offline / when rates are missing —
  // the operator can still type the right number manually.
  const fetchFxRate = async (from, to) => {
    if (!from || !to || from === to) return 1;
    try {
      const r = await api.get(`/api/procurement/fx-rate?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
      const rate = Number(r?.data?.rate);
      return Number.isFinite(rate) && rate > 0 ? rate : 1;
    } catch (_) {
      return 1;
    }
  };

  // Top-level currency change — convert every line's unit_price from the
  // previous currency to the new one via the FX endpoint. Empty / zero prices
  // are left alone. If the rate lookup fails we fall back to leaving values
  // unchanged (rate=1) so the operator can edit manually.
  const handleCurrencyChange = async (newCurrency) => {
    const prevCurrency = form.currency;
    if (newCurrency === prevCurrency) return;
    const rate = await fetchFxRate(prevCurrency, newCurrency);
    const converted = form.items.map(it => {
      const p = Number(it.unit_price);
      if (!Number.isFinite(p) || p === 0) return it;
      return { ...it, unit_price: Number((p * rate).toFixed(4)) };
    });
    setForm(prev => ({ ...prev, currency: newCurrency, items: converted }));
  };

  const pickKnownSku = async (idx, ks) => {
    // Pre-fill the line from a known-SKU click: unit_price from the most-recent
    // prior order with this supplier. If the historical order was in a different
    // currency than the current form, FX-convert via currency_monthly so the
    // operator sees a comparable number rather than a raw value mislabelled
    // with the wrong currency.
    if (!ks) { updateItem(idx, 'sku', ''); return; }
    const lastCcy = (ks.last_currency || form.currency || '').toUpperCase();
    const formCcy = (form.currency || '').toUpperCase();
    const rate = (lastCcy && formCcy && lastCcy !== formCcy) ? await fetchFxRate(lastCcy, formCcy) : 1;
    const unitPrice = Number(ks.last_unit_cost) ? Number((Number(ks.last_unit_cost) * rate).toFixed(4)) : 0;
    const next = [...form.items];
    next[idx] = {
      ...next[idx],
      sku: ks.sku,
      product_name: ks.product_name || next[idx].product_name,
      unit_price: unitPrice || next[idx].unit_price,
    };
    setForm({ ...form, items: next });
    // Fire stock fetch for the picked SKU (no-op if already cached).
    fetchStockForSku(ks.sku);
  };

  // Fetch stock breakdown for a SKU across all warehouses. Aggregates
  // sum(current_quantity − reserved_quantity) per warehouse_code so the form
  // can show the operator how much is already in-house before they decide
  // qty to order.
  const fetchStockForSku = async (sku) => {
    if (!sku) return;
    const key = sku.toUpperCase();
    if (stockBySku[key]) return; // already cached
    try {
      const r = await getInventoryBatches({ sku: key, limit: 1000 });
      const batches = r?.data?.batches || r?.batches || [];
      const perWh = {};
      // Pull the first non-empty image/product_name (enriched from product_master
      // in listInventoryBatches). All batches for one SKU share the same PM row,
      // so first-non-empty is reliable.
      let image_url = '';
      let product_name = '';
      let stock_uom = '';
      for (const b of batches) {
        const wc = b.warehouse_code || '?';
        const avail = (Number(b.current_quantity) || 0) - (Number(b.reserved_quantity) || 0);
        perWh[wc] = (perWh[wc] || 0) + Math.max(0, avail);
        if (!image_url && (b.thumbnail_url || b.image_url)) image_url = b.thumbnail_url || b.image_url;
        if (!product_name && b.product_name) product_name = b.product_name;
        if (!stock_uom && b.stock_uom) stock_uom = b.stock_uom;
      }
      const breakdown = Object.entries(perWh)
        .map(([warehouse_code, available]) => ({ warehouse_code, available }))
        .filter(x => x.available > 0)
        .sort((a, b) => b.available - a.available);
      const total = breakdown.reduce((s, x) => s + x.available, 0);
      setStockBySku(prev => ({ ...prev, [key]: { total, per_wh: breakdown, image_url, product_name, stock_uom } }));
      // Auto-fill the line's Description (product_name) from product_master when
      // the operator hasn't typed one. Previously only prior-supplier SKUs got a
      // name; a SKU not in this supplier's order history (e.g. SM0000022010 for
      // Fatehouse) showed blank even though product_master has it (Rishabh
      // 2026-06-16). Only fills empty names so a typed override is preserved.
      if (product_name) {
        setFormSilent(prev => ({
          ...prev,
          items: prev.items.map(it =>
            it.sku && it.sku.toUpperCase() === key && !it.product_name
              ? { ...it, product_name }
              : it
          )
        }));
      }
    } catch (_) {
      setStockBySku(prev => ({ ...prev, [key]: { total: 0, per_wh: [], image_url: '', product_name: '' } }));
    }
  };

  // Also fetch on free-typed SKU (debounced).
  useEffect(() => {
    const t = setTimeout(() => {
      for (const item of form.items) {
        if (item.sku) fetchStockForSku(item.sku);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items.map(i => i.sku).join('|')]);

  const openReceiptDialog = async () => {
    // Receipts are recorded against the SAVED PO — the backend caps the
    // receivable qty and prices the inventory batch from purchase_order_items,
    // not from what's typed on screen. If the operator edited qty/unit cost
    // (or added lines) without saving, recording now would silently use the
    // stale values, and the post-receipt refresh would clobber their typed
    // edits (Rishabh, PO #361, 2026-07-08). Offer to save first so the receipt
    // sees the updated values; new lines also gain their po_item_id this way.
    let dialogItems = form.items;
    if (isEdit && lineSig(form.items, form.currency) !== savedLineSigRef.current) {
      // Buttons carry the choice, so the old "OK = … / Cancel = …" legend that
      // the native popup needed is gone.
      const ok = await confirm({
        title: 'Save your changes first?',
        message: "This PO has unsaved edits to its line items (quantity, unit cost, or added lines). A receipt is always recorded against the last saved version, so your on-screen edits would be ignored.",
        confirmText: 'Save PO, then receive',
        cancelText: 'Go back',
        severity: 'warning',
      });
      if (!ok) return;
      const saved = await handleSave('stay');
      if (!saved) return; // save failed — the error banner explains why
      try {
        dialogItems = await refreshPoItemsFromServer();
      } catch (_) { dialogItems = form.items; }
    }
    // Pre-populate one row per line with qty_to_receive defaulted to the
    // remaining (ordered − received). Operator can lower for partials.
    const lineRows = dialogItems
      .filter(it => it.po_item_id && it.quantity > 0)
      .map(it => {
        const remaining = Math.max(0, Number(it.quantity) - Number(it.received_quantity));
        return {
          po_item_id: it.po_item_id,
          sku: it.sku,
          product_name: it.product_name,
          ordered: Number(it.quantity),
          already_received: Number(it.received_quantity),
          remaining,
          qty_to_receive: remaining,
          qty_to_accept: remaining,
          // Sticky: once the operator manually types in the Qty Accepted box
          // (e.g. set accept=480 of 500 received to reject 20 damaged units),
          // we stop auto-mirroring it from qty_to_receive — otherwise bumping
          // receive afterwards would silently clobber their decision.
          accept_manually_set: false,
          // Per-line warehouse override. Empty = inherit GR-header value at
          // submit time. Operator can override per line to split this receipt
          // across destinations (server creates one Pattern-A GR per warehouse).
          warehouse_code: '',
        };
      });
    // GIT is a virtual accounting bucket — never auto-fill it. Force operator
    // to pick a real destination. Alert in dialog explains.
    const poWarehouseCodeRaw = form.warehouse_code || '';
    const defaultedReceiptWarehouse = poWarehouseCodeRaw.toUpperCase() === 'GIT'
      ? ''
      : poWarehouseCodeRaw;
    setReceiptForm({
      receipt_date: new Date().toISOString().slice(0, 10),
      notes: '',
      // Manufacture POs: receipt warehouse is locked to the PO's manufacturer
      // warehouse (backend enforces it too — components deduct there).
      warehouse_code: isManufacturePo ? (form.warehouse_code || '') : defaultedReceiptWarehouse,
      skip_inventory: false,
      items: isManufacturePo
        ? lineRows.map(r => ({ ...r, batch_number: '', manufacturing_date: '', expiry_date: '' }))
        : lineRows,
    });
    setConfirmShortfall(false);
    setBackflushByLine({});
    if (isManufacturePo) {
      const today = new Date().toISOString().slice(0, 10);
      for (const r of lineRows) {
        if (r.qty_to_receive > 0) loadBackflushPreview(r.po_item_id, r.qty_to_receive, today);
      }
    }
    setError(null); setReceiptDialogOpen(true);
  };
  const updateReceiptLine = (idx, field, value) => {
    const next = [...receiptForm.items];
    // warehouse_code is a String pick; quantity fields are clamped numerics.
    if (field === 'warehouse_code') {
      next[idx] = { ...next[idx], warehouse_code: value || '' };
      setReceiptForm({ ...receiptForm, items: next });
      return;
    }
    // FG batch/expiry metadata (manufacture POs) — free-text/date fields.
    if (field === 'batch_number' || field === 'manufacturing_date' || field === 'expiry_date') {
      next[idx] = { ...next[idx], [field]: value || '' };
      setReceiptForm({ ...receiptForm, items: next });
      return;
    }
    // Manufacture POs may over-deliver (order 1,000 → produce 1,200): the
    // manufacturer's actual output IS the received qty, so no remaining-cap.
    const max = isManufacturePo ? Number.MAX_SAFE_INTEGER : next[idx].remaining;
    let v = Math.max(0, Math.min(max, Number(value) || 0));
    // Accepted-qty changes invalidate the loaded component plan for that line.
    if (isManufacturePo && (field === 'qty_to_accept' || field === 'qty_to_receive')) {
      const poItemId = next[idx].po_item_id;
      setBackflushByLine(prev => prev[poItemId]
        // Clearing the error lets the auto-recalculation retry for the new qty.
        ? { ...prev, [poItemId]: { ...prev[poItemId], stale: true, error: null } }
        : prev);
    }
    next[idx] = { ...next[idx], [field]: v };
    // qty_to_accept mirrors qty_to_receive by default — common case is
    // "received N, accept all N". Once the operator manually edits the Qty
    // Accepted box, accept_manually_set sticks and we stop overwriting so
    // their custom value (e.g. accept=480 of 500 received to reject 20) is
    // preserved even if they bump qty_to_receive afterwards.
    if (field === 'qty_to_accept') {
      next[idx].accept_manually_set = true;
    } else if (field === 'qty_to_receive') {
      if (!next[idx].accept_manually_set) {
        // Default mirror — no manual edit, accept follows receive.
        next[idx].qty_to_accept = v;
      } else if (next[idx].qty_to_accept > v) {
        // Manual edit preserved, but invariant accept<=receive must hold
        // (backend validates accepted+rejected<=received). Clamp down quietly
        // when receive shrinks below the custom accept value.
        next[idx].qty_to_accept = v;
      }
    }
    setReceiptForm({ ...receiptForm, items: next });
  };
  const submitReceipt = async () => {
    // Build items payload — keep per-line warehouse_code only when it
    // differs from the header (server falls back to header otherwise).
    const headerWh = receiptForm.warehouse_code || '';
    const skipInventory = !!receiptForm.skip_inventory;
    const visibleItems = receiptForm.items.filter(r => r.qty_to_receive > 0);
    // Resolve each line's effective warehouse: per-line override or header
    // fallback. Then decide how to ship this:
    //   - all lines resolve to the SAME warehouse  → single GR, header = that
    //     warehouse. Even if that's different from headerWh (operator
    //     overrode every line to a new warehouse), we promote the override
    //     into the header so goods_receipts.warehouse_code reflects where
    //     stock actually landed. Without this, the per-line override was
    //     silently dropped and the GR stored headerWh.
    //   - mixed warehouses → /split endpoint creates N Pattern-A GRs.
    const resolvedPerLine = visibleItems.map(r => (r.warehouse_code || headerWh));
    const uniqueWhs = Array.from(new Set(resolvedPerLine.filter(Boolean)));
    const isSplit = uniqueWhs.length > 1;
    const effectiveHeaderWh = (uniqueWhs.length === 1) ? uniqueWhs[0] : headerWh;
    const itemsPayload = visibleItems.map(r => {
      const base = {
        po_item_id: r.po_item_id,
        received_quantity: r.qty_to_receive,
        accepted_quantity: r.qty_to_accept,
        rejected_quantity: Math.max(0, r.qty_to_receive - r.qty_to_accept),
        // FG batch metadata (manufacture POs; backend ignores when absent).
        ...(isManufacturePo && r.batch_number ? { batch_number: r.batch_number } : {}),
        ...(isManufacturePo && r.manufacturing_date ? { manufacturing_date: r.manufacturing_date } : {}),
        ...(isManufacturePo && r.expiry_date ? { expiry_date: r.expiry_date } : {}),
      };
      // Carry the per-line override only when it differs from the effective
      // header we're about to send (single-GR case: header was promoted, so
      // the line and header now match — no override needed in payload).
      return (!isManufacturePo && r.warehouse_code && r.warehouse_code !== effectiveHeaderWh)
        ? { ...base, warehouse_code: r.warehouse_code }
        : base;
    });
    if (itemsPayload.length === 0) { setError('Enter at least one qty to receive.'); return; }

    // Fat-finger guard: over-production is normal on manufacture POs, but a
    // 2×+ overshoot (1,200 vs 12,000 typo) deserves an explicit confirm.
    if (isManufacturePo) {
      const bigOver = visibleItems.filter(r => r.ordered > 0 && r.qty_to_receive > 2 * r.ordered);
      if (bigOver.length) {
        const ok = await confirm({
          title: 'Produced quantity looks too high',
          message: 'More than double the ordered quantity for: '
            + bigOver.map(r => `${r.sku} — receiving ${r.qty_to_receive} against ${r.ordered} ordered`).join('; ')
            + '. Over-production is normal, but check this is not a typo.',
          confirmText: 'Yes, that is correct',
          cancelText: 'Let me check',
          severity: 'warning',
        });
        if (!ok) return;
      }
    }

    // Manufacture POs: every accepted line needs a fresh (non-stale) component
    // plan; the edited actuals ride along as backflush_components.
    let backflushPayload = null;
    if (isManufacturePo) {
      const staleOrMissing = visibleItems.filter(r =>
        r.qty_to_receive > 0 && (!backflushByLine[r.po_item_id]
          || backflushByLine[r.po_item_id].stale
          || backflushByLine[r.po_item_id].computed_for !== r.qty_to_receive
          || backflushByLine[r.po_item_id].loading
          || backflushByLine[r.po_item_id].error));
      if (staleOrMissing.length) {
        setError('Component consumption needs recalculating for: '
          + staleOrMissing.map(r => r.sku).join(', ')
          + ' — click Recalculate on those lines first.');
        return;
      }
      backflushPayload = visibleItems
        .filter(r => r.qty_to_receive > 0)
        .map(r => ({
          po_item_id: r.po_item_id,
          components: (backflushByLine[r.po_item_id]?.components || [])
            .map(c => ({ sku: c.component_sku, quantity: Number(c.quantity) || 0 })),
        }));
    }
    // A goods receipt is its own transaction and persists the moment it's
    // recorded — it is NOT staged behind "Save Changes", and clicking "Back"
    // will not discard it. Make that explicit so an exploratory entry can't
    // silently change stock (Rishabh, EF-59745, 2026-06-08). To reverse a
    // mistaken receipt, void/reject it from the Receipts list.
    const ok = await confirm({
      title: skipInventory ? 'Record receipt without adding stock?' : 'Record this goods receipt?',
      message: (skipInventory
        ? 'No stock will be added — use this only for goods already counted in inventory. '
        : '')
        + 'The receipt saves immediately as its own transaction, so Back will not undo it. To reverse it later you must void or reject the receipt from the Receipts list.',
      confirmText: 'Record receipt',
      severity: skipInventory ? 'warning' : 'info',
    });
    if (!ok) return;
    // Two-stage flow auto-link: if exactly one dispatched shipment is in
    // 'shipped' / 'arrived' state, attach this GR to it. Backend then posts
    // the reversing JE (Dr 1200 / Cr 1220) and closes the shipment when fully
    // received. When 0 or 2+ dispatched shipments, fall back to single-event
    // flow (cin7-ap-processor handles the inventory side at AP time).
    const autoShipmentId = (dispatchedShipments.length === 1) ? dispatchedShipments[0].id : null;
    // skip_inventory (set near the top): record the receipt + bump received
    // WITHOUT adding stock — for goods already in inventory. No batch is created,
    // so the multi-warehouse split path is irrelevant — use the single-GR endpoint.
    const useSplit = isSplit && !skipInventory && !isManufacturePo;
    const payload = {
      po_id: parseInt(editId),
      receipt_date: receiptForm.receipt_date,
      notes: receiptForm.notes,
      warehouse_code: isManufacturePo ? (form.warehouse_code || effectiveHeaderWh) : effectiveHeaderWh,
      items: itemsPayload,
      ...(skipInventory && !isManufacturePo ? { skip_inventory: true } : {}),
      ...(autoShipmentId ? { shipment_id: autoShipmentId } : {}),
      ...(backflushPayload ? { backflush_components: backflushPayload } : {}),
      ...(isManufacturePo && confirmShortfall ? { confirm_component_shortfall: true } : {}),
    };
    setRecordingReceipt(true);
    try {
      setWarning(null);
      const created = await (useSplit ? createGoodsReceiptsSplit(payload) : createGoodsReceipt(payload));
      setReceiptDialogOpen(false);
      // Refresh PO (received_quantity has bumped) and receipts list.
      await refreshPoItemsFromServer();
      await loadReceipts();
      setSuccess('Receipt recorded');
      setWarning(skippedCostRowsWarning(skippedCostRows(created), 'Receipt recorded'));
    } catch (e) {
      setError(extractApiError(e, 'Failed to record receipt'));
    } finally {
      setRecordingReceipt(false);
    }
  };

  // mode='back' navigates to the list after a brief success toast; 'stay' keeps
  // the operator on the edit page so they can keep iterating (e.g. add a
  // receipt, then save again). 'Create PO' always uses 'back' since there's no
  // edit-mode page to stay on yet — newly-created POs return to the list with
  // a success message.
  // Reopen a closed PO so it can be edited + saved. After reopening, reload the
  // edit page to pull the now-active status and drop the read-only lock.
  const handleReopenPo = async () => {
    if (!editId) return;
    try {
      await api.post(`/api/procurement/purchase-orders/${editId}/reopen`);
      setPoClosedNotice(null);
      setSuccess('PO reopened — you can now edit and save.');
      setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reopen PO');
    }
  };

  // Clear a PHANTOM received quantity — a cin7-imported number on the PO line
  // with no goods-receipt and no stock behind it. The backend re-checks the
  // gate server-side and refuses (409) if any real receipt/batch/consumption
  // exists, so this can only ever clear a genuinely empty "received" number.
  // After clearing, reload to pull the reopened, zero-received PO.
  const handleUnreceivePhantom = async () => {
    if (!editId) return;
    setUnreceiving(true); setError(null);
    try {
      await api.post(`/api/procurement/purchase-orders/${editId}/unreceive`);
      setUnreceiveConfirmOpen(false);
      setSuccess('Phantom received quantity cleared — PO reopened. You can now record the real receipt.');
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to un-receive PO');
      setUnreceiveConfirmOpen(false);
    } finally {
      setUnreceiving(false);
    }
  };

  // Returns true when the save persisted, false otherwise — the
  // save-before-receipt path in openReceiptDialog depends on this.
  const handleSave = async (mode = 'back') => {
    if (poClosedNotice) { setError('This PO is closed. Click Reopen before saving changes.'); return false; }
    if (!form.supplier_id) { setError('Pick a supplier first.'); return false; }
    if (!form.po_date) { setError('PO Date is required.'); return false; }
    if (!form.items.length || form.items.every(i => !i.sku)) {
      setError('At least one line item with a SKU is required.'); return false;
    }
    if (form.po_type === 'manufacture') {
      if (!form.brand_id) { setError('Manufacture POs need a brand (production BOMs are brand-scoped).'); return false; }
      if (!form.warehouse_code) { setError('Manufacture POs need a receipt warehouse — the manufacturer\'s site where production happens.'); return false; }
    }
    setSaving(true); setError(null);
    try {
      // Strip empty items + ensure brand_id is sent as integer (or 0).
      const payload = {
        ...form,
        brand_id: form.brand_id ? parseInt(form.brand_id) : 0,
        subsidiary_id: form.subsidiary_id ? parseInt(form.subsidiary_id) : 0, // #2b
        items: form.items.filter(i => i.sku && Number(i.quantity) > 0),
      };
      if (isEdit) {
        await updatePurchaseOrder(editId, payload);
        setSuccess('Purchase order updated');
      } else {
        const r = await createPurchaseOrder(payload);
        setSuccess(`Purchase order created: ${r?.data?.po_number || ''}`);
      }
      // The on-screen lines are now the persisted lines — re-baseline the
      // unsaved-edits guard used by "Record Receipt".
      savedLineSigRef.current = lineSig(payload.items, payload.currency);
      // Everything on screen is saved, so leaving is now safe.
      setUserTouched(false);
      if (mode === 'stay' && isEdit) {
        // Stay on page — clear the success message after a beat so the bar
        // doesn't keep claiming "updated" through subsequent edits.
        setTimeout(() => setSuccess(null), 2500);
      } else {
        setTimeout(() => navigate(listBackTo), 800);
      }
      return true;
    } catch (e) {
      setError(extractApiError(e, 'Failed to save'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Receipt-aware delete: backend rejects with 409/PO_HAS_RECEIPTS when goods
  // have been received, but we also disable the button client-side so the
  // operator sees the constraint before clicking.
  const totalReceivedQty = form.items.reduce((acc, i) => acc + Number(i.received_quantity || 0), 0);
  const hasReceipts = totalReceivedQty > 0;

  const handleDelete = async () => {
    setDeleting(true); setError(null);
    try {
      await deletePurchaseOrder(editId);
      setDeleteConfirmOpen(false);
      setSuccess('Purchase order deleted');
      setTimeout(() => navigate(listBackTo), 600);
    } catch (e) {
      setError(extractApiError(e, 'Failed to delete'));
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>Loading…</Box>;
  }

  // Set of SKUs already known to this supplier (for "Previously ordered" badge
  // inside the picker and to surface them at the top of the dropdown).
  const knownSkuSet = new Set(knownSkus.map(k => k.sku.toLowerCase()));
  // Dropdown options: previously-ordered SKUs first (with order history + price),
  // then every other Products-module SKU under an "All products" group.
  const skuOptions = [
    ...knownSkus.map(k => ({ ...k, _group: 'Previously ordered' })),
    ...catalogSkus
      .filter(c => c.sku && !knownSkuSet.has(c.sku.toLowerCase()))
      .map(c => ({ ...c, _group: 'All products' })),
  ];

  // Product stock unit per SKU, sourced from product_master via the picker lists
  // (known-skus + catalog) — so a line shows its unit (e.g. yards) even when the
  // SKU has NO stock yet (a brand-new material you're purchasing). Stock-based
  // lookup (stockBySku) is only a fallback for SKUs not in either picker list.
  const uomBySku = {};
  for (const k of knownSkus) { const s = (k.sku || '').toLowerCase(); if (s && k.stock_uom) uomBySku[s] = k.stock_uom; }
  for (const c of catalogSkus) { const s = (c.sku || '').toLowerCase(); if (s && c.stock_uom && !uomBySku[s]) uomBySku[s] = c.stock_uom; }

  return (
    // onChangeCapture catches every real edit bubbling out of the form fields
    // below; that is what makes the unsaved-changes guard accurate without
    // deep-comparing state that the supplier autofill effect mutates.
    <Box sx={{ p: 2, pb: 12 /* room for sticky action bar */ }} onChangeCapture={markTouchedFromDom}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => guardedLeave(() => navigate(listBackTo))}><ArrowBackIcon /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {isEdit ? `Edit Purchase Order ${poNumber || `#${editId}`}` : 'New Purchase Order'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedSupplier ? `${selectedSupplier.supplier_name} · ${form.currency}` : 'Pick a supplier and brand to begin'}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Chip label={`Total: ${formatCurrency(totals.total, form.currency)}`} color="primary" sx={{ fontWeight: 700, fontSize: 14 }} />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {warning && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>{warning}</Alert>}
      {poClosedNotice && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" variant="outlined" onClick={handleReopenPo}>Reopen</Button>}
        >
          {poClosedNotice}
        </Alert>
      )}

      {/* Top row: Supplier · Brand · Currency · Dates */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <SupplierPicker
                value={form.supplier_id}
                onChange={(sid) => {
                  // Changing the supplier re-enables payment-terms autofill so the
                  // new supplier's terms flow in (unless the operator later edits
                  // the field by hand). On edit-load the flag is forced true to
                  // preserve the saved terms; switching supplier is an explicit
                  // signal the terms should follow the new supplier.
                  if (sid !== form.supplier_id) setPaymentTermsManuallyEdited(false);
                  setSupplierPickedByOperator(true);
                  setForm({ ...form, supplier_id: sid });
                }}
                suppliers={suppliers}
                label="Supplier"
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand</InputLabel>
                <Select value={form.brand_id} label="Brand"
                  onChange={(e) => setForm({ ...form, brand_id: e.target.value })}>
                  <MenuItem value=""><em>Company-level (no brand)</em></MenuItem>
                  {brands.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* #2b: billing entity — renders under "Bill To" on the PO PDF */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Bill To (Subsidiary)</InputLabel>
                <Select value={form.subsidiary_id} label="Bill To (Subsidiary)"
                  onChange={(e) => setForm({ ...form, subsidiary_id: e.target.value })}>
                  <MenuItem value=""><em>Default (primary entity)</em></MenuItem>
                  {subsidiaries.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.legal_name || s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select value={form.currency} label="Currency"
                  onChange={(e) => handleCurrencyChange(e.target.value)}>
                  {['USD','GBP','EUR','AUD','SGD','CAD','NZD','CHF','HKD','JPY','CNY'].map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth size="small" type="date" label="PO Date"
                InputLabelProps={{ shrink: true }} required
                value={form.po_date} onChange={(e) => setForm({ ...form, po_date: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth size="small" type="date" label="Expected"
                InputLabelProps={{ shrink: true }}
                value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              {/* Contract manufacturing: the supplier PRODUCES the line SKU (an L1
                  assembly with a production BOM) at the PO warehouse, consuming
                  component stock held there. Line price = conversion fee only.
                  Editable until receiving starts (Rishabh 2026-07-06) — after
                  the first receipt the type is locked because the two types
                  post inventory completely differently. Backend enforces the
                  same rule (PO_TYPE_LOCKED). */}
              <FormControl fullWidth size="small" disabled={isEdit && hasReceipts}>
                <InputLabel>PO Type</InputLabel>
                <Select value={form.po_type || 'standard'} label="PO Type"
                  onChange={(e) => setForm({ ...form, po_type: e.target.value })}>
                  <MenuItem value="standard">Standard (buy goods)</MenuItem>
                  <MenuItem value="manufacture">Manufacture (contract production)</MenuItem>
                </Select>
                {isEdit && hasReceipts && (
                  <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5, mt: 0.25 }}>
                    Locked — goods already received on this PO
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {(form.po_type === 'manufacture') && (
              <Grid item xs={12} md={9}>
                <Alert severity="info" sx={{ py: 0 }}>
                  Manufacture PO: line SKU = the finished good (needs a master production BOM); unit price = the
                  manufacturer's conversion fee per unit. Receiving this PO creates the finished-good stock at the
                  warehouse below and consumes its components there. Brand and warehouse are required.
                </Alert>
              </Grid>
            )}
          </Grid>

          {/* Supplier + Delivery address — two side-by-side cards shown only
              after a supplier is picked. Supplier side is read-only (driven by
              suppliers table). Delivery side has a warehouse picker that
              auto-fills the address; operator can still tweak the textarea. */}
          {selectedSupplier && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 1 }}>
                    Supplier Address · {knownSkus.length} SKU{knownSkus.length === 1 ? '' : 's'} prior
                  </Typography>
                  {/* Each field shows the supplier master value as a placeholder
                      ("hint"); typing overrides it for THIS PO only. Empty value
                      = use master. Backend layers override-on-top in print-service. */}
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Contact"
                        value={form.supplier_contact_name_override}
                        placeholder={selectedSupplier.primary_contact_name || '(none on supplier)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_contact_name_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Email"
                        value={form.supplier_contact_email_override}
                        placeholder={selectedSupplier.primary_contact_email || '(none on supplier)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_contact_email_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Phone"
                        value={form.supplier_contact_phone_override}
                        placeholder={selectedSupplier.primary_contact_phone || '(none on supplier)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_contact_phone_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Default Terms</Typography>
                      <Typography variant="body2" sx={{ pt: 0.5 }}>{selectedSupplier.payment_terms || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Address Line 1"
                        value={form.supplier_address_line1_override}
                        placeholder={selectedSupplier.billing_address_line1 || '(none on supplier)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_address_line1_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Address Line 2"
                        value={form.supplier_address_line2_override}
                        placeholder={selectedSupplier.billing_address_line2 || '(optional)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_address_line2_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="City"
                        value={form.supplier_city_override}
                        placeholder={selectedSupplier.billing_city || '(none on supplier)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_city_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="State / Province"
                        value={form.supplier_state_override}
                        placeholder={selectedSupplier.billing_state || '(optional)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_state_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Postal Code"
                        value={form.supplier_postal_code_override}
                        placeholder={selectedSupplier.billing_postal_code || '(none on supplier)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_postal_code_override: e.target.value })} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" label="Country"
                        value={form.supplier_country_override}
                        placeholder={selectedSupplier.billing_country || '(none on supplier)'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setForm({ ...form, supplier_country_override: e.target.value })} />
                    </Grid>
                  </Grid>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.disabled', fontSize: 10 }}>
                    Editing here overrides supplier details for this PO only. Leave a field blank to use the supplier's master value (shown in grey).
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 1 }}>
                    Receipt Warehouse
                  </Typography>
                  {(() => {
                    // Always render the picker so operators can override the
                    // supplier's default_warehouse_code or defer the choice to
                    // goods-receipt time. Backend allows NULL warehouse_code at
                    // creation (assertPhysicalShipTo skips when falsy).
                    const wh = warehouses.find(w => w.code === form.warehouse_code);
                    const hasDefault = !!selectedSupplier?.default_warehouse_code;
                    return (
                      <>
                        {!hasDefault && (
                          <Alert severity="warning" sx={{ mb: 1, py: 0.5 }} icon={false}>
                            <Typography variant="caption" sx={{ fontSize: 11 }}>
                              Supplier has no default warehouse set.
                              <strong> Set it on the supplier record</strong> so future POs auto-fill.
                            </Typography>
                          </Alert>
                        )}
                        <FormControl fullWidth size="small">
                          <InputLabel>Receipt warehouse</InputLabel>
                          <Select value={form.warehouse_code} label="Receipt warehouse"
                            onChange={(e) => setForm({ ...form, warehouse_code: e.target.value })}>
                            <MenuItem value=""><em>None — decide later</em></MenuItem>
                            {warehouses.map(w => (
                              <MenuItem key={w.code} value={w.code}>
                                {w.code} — {w.name}{w.country ? ` · ${w.country}` : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {wh && (wh.address_line1 || wh.city || wh.country) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11, lineHeight: 1.4, mt: 1 }}>
                            {[wh.address_line1, wh.address_line2].filter(Boolean).join(', ')}
                            {wh.city || wh.state ? <><br />{[wh.city, wh.state, wh.postal_code].filter(Boolean).join(', ')}</> : null}
                            {wh.country ? <><br />{wh.country}</> : null}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.disabled', fontSize: 10 }}>
                          {hasDefault ? 'Defaulted from supplier · branch transfer moves goods onward' : 'Pick a warehouse or leave blank · branch transfer moves goods onward'}
                        </Typography>
                      </>
                    );
                  })()}
                </Paper>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Line items */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Line Items</Typography>
          {!form.supplier_id && (
            <Alert severity="info" sx={{ mb: 1.5 }}>Pick a supplier above to load their previously-ordered SKUs (they'll appear at the top of the SKU picker).</Alert>
          )}
          {form.items.map((item, idx) => {
            const lineTotal = computeLineTotal(item);
            const skuKey = (item.sku || '').toUpperCase();
            const stockInfo = stockBySku[skuKey];
            const thumbUrl = stockInfo?.image_url || '';
            return (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  {/* Product thumbnail (left rail) — pulled from product_master via
                      the inventory_batches enrichment. Renders a placeholder until
                      the SKU is recognised. */}
                  <Box sx={{ flex: '0 0 72px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {thumbUrl ? (
                      <Box component="img" src={thumbUrl} alt={item.sku}
                        sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'common.white' }}
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <Box sx={{ width: 72, height: 72, borderRadius: 1, border: '1px dashed', borderColor: 'divider',
                                 bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9, textAlign: 'center', px: 0.5 }}>
                          {item.sku ? 'no image' : '—'}
                        </Typography>
                      </Box>
                    )}
                    {item.sku && knownSkuSet.has(item.sku.toLowerCase()) && (
                      <Chip label="Prior" size="small" color="success" variant="outlined"
                        sx={{ mt: 0.5, fontSize: 9, height: 16, '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                  </Box>

                  {/* Content area — two rows of fields */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Row 1 — identity: SKU + Description + customs (HSN + COO). */}
                <Grid container spacing={1.5} alignItems="flex-start">
                  <Grid item xs={12} md={3.5}>
                    <Autocomplete
                      size="small" freeSolo
                      options={skuOptions}
                      groupBy={(o) => o._group || 'All products'}
                      filterOptions={skuFilter}
                      getOptionLabel={(o) => (typeof o === 'string' ? o : o?.sku || '')}
                      value={item.sku || null}
                      onChange={(e, v) => pickKnownSku(idx, v)}
                      onInputChange={(e, v, reason) => {
                        // MUI fires 'reset' with no operator involved: on mount
                        // and whenever the value prop changes (the load, a picked
                        // option). It writes back the sku the line already holds,
                        // and routing that through updateItem -> setForm marked
                        // every loaded PO dirty before anyone touched it, so Back
                        // prompted "Discard your changes?" on a view-only PO
                        // (Rishabh 2026-09-04). Only operator input counts.
                        if (reason === 'reset') return;
                        updateItem(idx, 'sku', v || '');
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.sku} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>{option.sku}</Typography>
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {option.product_name || '(no name)'}
                            </Typography>
                          </Box>
                          {option.times_ordered != null && (
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {form.currency} {Number(option.last_unit_cost || 0).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.disabled' }}>
                                ordered {option.times_ordered}× · last {String(option.last_order_date || '').slice(0, 10)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="SKU" required
                          placeholder={form.supplier_id ? `${knownSkus.length} prior · or any product` : 'Pick supplier first'} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4.5}>
                    <TextField fullWidth size="small" label="Description"
                      value={item.product_name}
                      onChange={(e) => updateItem(idx, 'product_name', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label="HSN"
                      value={item.hs_code}
                      onChange={(e) => updateItem(idx, 'hs_code', e.target.value)}
                      placeholder="e.g. 33049990" />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label="COO"
                      value={item.country_of_origin}
                      onChange={(e) => updateItem(idx, 'country_of_origin', e.target.value)}
                      placeholder="CN / IN / IT" />
                  </Grid>
                </Grid>

                {/* Row 2 — quantities + pricing breakdown. Column widths sum to
                    exactly 12 at md+ so the row doesn't slop. xs widths tile to
                    12+12=24 → two clean rows on mobile, no orphan cells. */}
                <Grid container spacing={1.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
                  <Grid item xs={4} md={1.2}>
                    <TextField fullWidth size="small" type="number" label="Qty Ord" required
                      inputProps={{ min: 0 }}
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                    {/* Product's stock unit (from product_master.stock_uom) so the
                        buyer knows what unit they're ordering in — e.g. rubber in
                        yards. Sourced from the picker lists (product_master) so it
                        shows even for a brand-new SKU with NO stock; falls back to
                        the stock lookup. Only shown when it isn't the default 'units'. */}
                    {item.sku && (() => {
                      const u = uomBySku[item.sku.toLowerCase()] || stockBySku[item.sku.toUpperCase()]?.stock_uom;
                      return u && u !== 'units' ? (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'primary.main', mt: 0.25, fontWeight: 600 }}>
                          unit: {u}
                        </Typography>
                      ) : null;
                    })()}
                    {/* Stock-on-hand hint — shows total across all warehouses
                        plus a per-warehouse breakdown so operator knows how
                        much already exists before deciding qty to order. */}
                    {item.sku && stockBySku[item.sku.toUpperCase()] && (() => {
                      const s = stockBySku[item.sku.toUpperCase()];
                      if (s.total === 0) {
                        return (
                          <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'error.dark', mt: 0.5, fontStyle: 'italic' }}>
                            Out of stock — 0 across all warehouses
                          </Typography>
                        );
                      }
                      return (
                        <Tooltip arrow title={
                          <Box sx={{ fontSize: 11 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                              Stock by warehouse
                            </Typography>
                            {s.per_wh.map(w => (
                              <Box key={w.warehouse_code} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                <span>{w.warehouse_code}</span>
                                <span style={{ fontFamily: 'monospace' }}>{Number(w.available).toLocaleString()}</span>
                              </Box>
                            ))}
                          </Box>
                        }>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'success.dark', mt: 0.5, cursor: 'help', borderBottom: '1px dotted', borderColor: 'success.light' }}>
                            {Number(s.total).toLocaleString()} avail across {s.per_wh.length} WH
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </Grid>
                  <Grid item xs={4} md={1.2}>
                    {/* Read-only — updated by goods_receipts. In edit mode it
                        reflects sum of received_quantity from receipts. */}
                    <TextField fullWidth size="small" type="number" label="Qty Recv"
                      InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50' } }}
                      value={item.received_quantity || 0}
                      helperText={Number(item.received_quantity) < Number(item.quantity)
                        ? `${Number(item.quantity) - Number(item.received_quantity)} remaining`
                        : (Number(item.received_quantity) > 0 ? 'fully received' : '')}
                      FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
                  </Grid>
                  <Grid item xs={4} md={1.5}>
                    {/* Unit cost — anchor of the row, wider on md+. */}
                    <TextField fullWidth size="small" type="number" label="Unit Cost"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={item.unit_price}
                      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 11 }}>{form.currency}</InputAdornment> }}
                      onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={3} md={1}>
                    <TextField fullWidth size="small" type="number" label="Disc %"
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      value={item.discount_percent}
                      onChange={(e) => updateItem(idx, 'discount_percent', parseFloat(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={3} md={1.4}>
                    {/* Computed: gross × disc% — read-only display so operator
                        sees the actual discount amount, not just the percentage. */}
                    <TextField fullWidth size="small" label="Disc Total"
                      InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50' } }}
                      value={(() => {
                        const q = Number(item.quantity) || 0;
                        const p = Number(item.unit_price) || 0;
                        const d = Number(item.discount_percent) || 0;
                        return formatCurrency(q * p * (d / 100), form.currency);
                      })()} />
                  </Grid>
                  <Grid item xs={3} md={1}>
                    <TextField fullWidth size="small" type="number" label="Tax %"
                      inputProps={{ min: 0, step: 0.1 }}
                      value={item.tax_percent}
                      onChange={(e) => updateItem(idx, 'tax_percent', parseFloat(e.target.value) || 0)} />
                  </Grid>
                  {/* Subtotal/Landed/Tax/Line panel — gets the remaining 4.7/12
                      so monetary values aren't squeezed against the row edge. */}
                  <Grid item xs={9} md={4.7}>
                    {(() => {
                      // Pre-tax subtotal (qty × unit − line discount) — useful for
                      // operators reading a quote, since "Line Total" includes tax.
                      const q = Number(item.quantity) || 0;
                      const p = Number(item.unit_price) || 0;
                      const d = Number(item.discount_percent) || 0;
                      const subtotal = q * p * (1 - d / 100);
                      // Landed unit cost: unit_price + this line's share of
                      // (freight + customs) allocated pro-rata by value. Shows
                      // only when header has landed costs entered.
                      const landedPool = (Number(form.freight_cost) || 0) + (Number(form.customs_duty) || 0);
                      let landedUnit = null;
                      if (landedPool > 0 && q > 0) {
                        const allSubtotals = form.items.map(it => {
                          const qq = Number(it.quantity) || 0;
                          const pp = Number(it.unit_price) || 0;
                          const dd = Number(it.discount_percent) || 0;
                          return qq * pp * (1 - dd / 100);
                        });
                        const totalValue = allSubtotals.reduce((s, x) => s + x, 0);
                        if (totalValue > 0) {
                          const lineShare = landedPool * (subtotal / totalValue);
                          landedUnit = p + lineShare / q;
                        }
                      }
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right', color: 'text.secondary' }}>
                              {formatCurrency(subtotal, form.currency)}
                            </Typography>
                          </Box>
                          {landedUnit !== null && (
                            <Tooltip title={`Unit cost + (freight + customs share / qty). Pro-rata by line value.`} arrow>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: 'help' }}>
                                <Typography variant="caption" color="info.main">Landed/Unit</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right', color: 'info.main', borderBottom: '1px dotted', borderColor: 'info.light' }}>
                                  {formatCurrency(landedUnit, form.currency)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography variant="caption" color="text.secondary">Line Total</Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', flex: 1, textAlign: 'right' }}>
                              {formatCurrency(lineTotal, form.currency)}
                            </Typography>
                            <IconButton size="small" color="error" onClick={() => removeItem(idx)} disabled={form.items.length === 1}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })()}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Line notes (optional)"
                      value={item.notes}
                      onChange={(e) => updateItem(idx, 'notes', e.target.value)}
                      placeholder="e.g. urgent, swap pack size, sample batch" />
                  </Grid>
                </Grid>
                  </Box>{/* /content area */}
                </Box>{/* /flex thumbnail-row */}
              </Paper>
            );
          })}
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addItem}>Add Line Item</Button>

          {/* Totals */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ minWidth: 280 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, py: 0.25 }}>
                <span>Items / Total Qty</span><strong>{form.items.length} / {totals.qty}</strong>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, py: 0.25 }}>
                <span>Gross</span><strong>{formatCurrency(totals.gross, form.currency)}</strong>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, py: 0.25, color: 'text.secondary' }}>
                <span>Discount</span><strong>−{formatCurrency(totals.discount, form.currency)}</strong>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, py: 0.25, color: 'text.secondary' }}>
                <span>Tax</span><strong>{formatCurrency(totals.tax, form.currency)}</strong>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, py: 0.5 }}>
                <strong>Total</strong>
                <strong>{formatCurrency(totals.total, form.currency)}</strong>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Manufacture POs: production-BOM component readiness at the manufacturer
          warehouse — required vs available vs on-order vs shortfall. */}
      {isEdit && form.po_type === 'manufacture' && (
        <ComponentReadinessPanel poId={editId} />
      )}

      {/* Receipts (edit mode only) — partial receive support. Each receipt
          increments purchase_order_items.received_quantity and creates inventory
          batches at the supplier-branch warehouse. When sum(received) >= sum(ordered),
          the backend stamps actual_delivery_date + status='received'. */}
      {isEdit && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <PoDocuments poId={editId} canEdit />
          </CardContent>
        </Card>
      )}

      {isEdit && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Receipts</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    const active = receipts.filter(r => r.inspection_status !== 'rejected');
                    const voided = receipts.length - active.length;
                    const head = active.length === 0
                      ? 'No active receipts'
                      : `${active.length} active receipt${active.length === 1 ? '' : 's'}`;
                    const voidSuffix = voided > 0 ? ` (${voided} voided)` : '';
                    const ordered = form.items.reduce((s, i) => s + Number(i.quantity || 0), 0);
                    const received = form.items.reduce((s, i) => s + Number(i.received_quantity || 0), 0);
                    return `${head}${voidSuffix} · ${received.toLocaleString()} / ${ordered.toLocaleString()} units received`;
                  })()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Phantom un-receive: only when there are NO receipt records but a
                    received qty sits on the line (cin7-import artifact). Backend
                    re-validates before it will clear anything. */}
                {receipts.length === 0 && totalReceivedQty > 0 && (
                  <Button variant="outlined" color="warning" size="small" onClick={() => setUnreceiveConfirmOpen(true)}>
                    Un-receive
                  </Button>
                )}
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openReceiptDialog}>
                  Record Receipt
                </Button>
              </Box>
            </Box>
            {receipts.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>GR #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total Qty</TableCell>
                      <TableCell>Warehouse</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receipts.map(r => {
                      // Voided/rejected GRs stay listed for audit but visually
                      // de-emphasized — they no longer contribute to PO totals.
                      const isRejected = r.inspection_status === 'rejected';
                      return (
                        <TableRow
                          key={r.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            opacity: isRejected ? 0.55 : 1,
                            '& td': isRejected ? { textDecoration: 'line-through' } : {},
                          }}
                          onClick={() => navigate(`/procurement/goods-receipts/${r.id}`)}
                        >
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.grn_number || r.gr_number || `#${r.id}`}</TableCell>
                          <TableCell>{(r.receipt_date || '').slice(0, 10)}</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                            {Number(r.total_received_qty || r.total_received || r.received_quantity || 0).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }} title={r.warehouse_code || ''}>
                            {r.warehouse_name || r.warehouse_code || '—'}
                          </TableCell>
                          <TableCell sx={{ textDecoration: 'none !important' }}>
                            <StatusBadge status={r.inspection_status || 'pending'} size="small" />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.notes}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payments (edit mode) — installments against this PO. Distinct from
          the supplier+invoice payments system; this is direct-against-PO. */}
      {isEdit && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>Payments</Typography>
              <Typography variant="caption" color="text.secondary">
                {(() => {
                  const paid = payments.reduce((s, p) => s + (Number(p.amount_in_po_currency) > 0 ? Number(p.amount_in_po_currency) : Number(p.amount || 0)), 0);
                  return `${payments.length} payment${payments.length === 1 ? '' : 's'} · ${formatCurrency(paid, form.currency)} paid of ${formatCurrency(totals.total + (Number(form.freight_cost) || 0) + (Number(form.customs_duty) || 0), form.currency)}`;
                })()}
              </Typography>
            </Box>
            {payments.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map(p => (
                      <TableRow key={p.id} hover>
                        <TableCell>{(p.payment_date || '').slice(0, 10)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                          {formatCurrency(p.amount, p.currency || form.currency)}
                          {p.currency && p.currency !== form.currency && Number(p.amount_in_po_currency) > 0 && (
                            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                              → {formatCurrency(p.amount_in_po_currency, form.currency)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{(p.method || '').replace(/_/g, ' ')}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.reference}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{p.notes}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => openEditPayment(p)} title="Edit payment">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => removePayment(p.id)} title="Delete payment">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Paper ref={addPaymentRef} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 1 }}>
                Add Payment
              </Typography>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={6} md={2}>
                  <TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Date"
                    value={newPayment.payment_date}
                    onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })} />
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <TextField fullWidth size="small" type="number" label="Amount"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                    onBlur={(e) => { const c = newPayment.currency || form.currency; if (c !== form.currency) fetchEquivalent(e.target.value, c); }} />
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select value={newPayment.currency || form.currency} label="Currency"
                      onChange={(e) => {
                        const c = e.target.value;
                        setNewPayment((prev) => ({ ...prev, currency: c, amount_in_po_currency: c === form.currency ? '' : prev.amount_in_po_currency }));
                        if (c !== form.currency) fetchEquivalent(newPayment.amount, c);
                      }}>
                      {['USD','GBP','EUR','AUD','SGD','CAD','NZD','CHF','HKD','JPY','CNY'].map(c => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {(newPayment.currency && newPayment.currency !== form.currency) && (
                  <Grid item xs={12} md={2}>
                    <TextField fullWidth size="small" type="number" label={`Applied to PO (${form.currency})`}
                      inputProps={{ min: 0, step: 0.01 }}
                      value={newPayment.amount_in_po_currency}
                      onChange={(e) => setNewPayment({ ...newPayment, amount_in_po_currency: e.target.value })}
                      helperText="auto-filled at today's rate — edit to match your bank" />
                  </Grid>
                )}
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Method</InputLabel>
                    <Select value={newPayment.method} label="Method"
                      onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })}>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="wire">Wire</MenuItem>
                      <MenuItem value="ach">ACH</MenuItem>
                      <MenuItem value="check">Check</MenuItem>
                      <MenuItem value="credit_card">Credit Card</MenuItem>
                      <MenuItem value="cash">Cash</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth size="small" label="Reference"
                    value={newPayment.reference}
                    onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                    placeholder="Bank ref / cheque #" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth size="small" label="Invoice #"
                    value={newPayment.invoice_number}
                    onChange={(e) => setNewPayment({ ...newPayment, invoice_number: e.target.value })}
                    placeholder="Supplier invoice #" />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth size="small" label="Notes"
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                    placeholder="e.g. deposit, balance, milestone 2" />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button fullWidth variant="contained" size="small" onClick={addPayment} disabled={savingPayment}>
                    {savingPayment ? '…' : 'Add'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Record Receipt dialog */}
      <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isManufacturePo ? 'Record Production Receipt' : 'Record Goods Receipt'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {isManufacturePo && (
            <Alert severity="info" sx={{ mb: 1, py: 0 }}>
              This receipt is the production event: it adds the finished good at the manufacturer warehouse
              and consumes the components below. Adjust the actual consumption for wastage before recording.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="date" label="Receipt Date"
                InputLabelProps={{ shrink: true }}
                value={receiptForm.receipt_date}
                onChange={(e) => setReceiptForm({ ...receiptForm, receipt_date: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={3}>
              {/* Destination warehouse for this whole receipt. Defaults from
                  PO.warehouse_code on dialog open; operator can override —
                  EXCEPT manufacture POs, where it's locked to the manufacturer
                  warehouse (components deduct there; backend enforces). */}
              <FormControl fullWidth size="small" disabled={isManufacturePo}>
                <InputLabel>Destination warehouse</InputLabel>
                <Select
                  label="Destination warehouse"
                  value={receiptForm.warehouse_code || ''}
                  onChange={(e) => setReceiptForm({ ...receiptForm, warehouse_code: e.target.value })}
                >
                  <MenuItem value=""><em>None — fall through to PO</em></MenuItem>
                  {warehouses.map(w => (
                    <MenuItem key={w.code} value={w.code}>
                      {w.code} — {w.name}{w.country ? ` · ${w.country}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Notes"
                value={receiptForm.notes}
                onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })}
                placeholder="e.g. partial 1 of 2, container A only, condition notes" />
            </Grid>
            {/* GIT-warehouse nudge — legacy CIN7 POs carry warehouse_code='GIT'.
                openReceiptDialog deliberately leaves picker empty so operator
                must pick a real destination. */}
            {String(form.warehouse_code || '').toUpperCase() === 'GIT' && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  This PO ships to <strong>GIT</strong> (Goods In Transit — accounting bucket only).
                  Pick the actual receiving warehouse above before recording the receipt.
                </Alert>
              </Grid>
            )}
          </Grid>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Ordered</TableCell>
                  <TableCell align="right">Already Received</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>Qty to Receive</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>Qty Accepted</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Warehouse</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receiptForm.items.map((r, idx) => (
                  <React.Fragment key={r.po_item_id}>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.sku}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.product_name}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.ordered.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{r.already_received.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: r.remaining === 0 ? 'success.main' : 'warning.dark' }}>
                      {r.remaining.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number"
                        inputProps={{ min: 0, ...(isManufacturePo ? {} : { max: r.remaining }), style: { textAlign: 'right' } }}
                        value={r.qty_to_receive}
                        onChange={(e) => updateReceiptLine(idx, 'qty_to_receive', e.target.value)}
                        disabled={!isManufacturePo && r.remaining === 0} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number"
                        inputProps={{ min: 0, max: r.qty_to_receive, style: { textAlign: 'right' } }}
                        value={r.qty_to_accept}
                        onChange={(e) => updateReceiptLine(idx, 'qty_to_accept', e.target.value)}
                        disabled={r.qty_to_receive === 0} />
                    </TableCell>
                    <TableCell>
                      {isManufacturePo ? (
                        <Typography variant="caption" color="text.secondary">{form.warehouse_code} (locked)</Typography>
                      ) : (
                      /* Per-line warehouse — defaults to header (shown via
                          "(use header)"). Pick a different warehouse to split
                          this receipt across destinations; server creates one
                          Pattern-A GR per destination. */
                      <FormControl size="small" fullWidth>
                        <Select
                          value={r.warehouse_code || ''}
                          displayEmpty
                          onChange={(e) => updateReceiptLine(idx, 'warehouse_code', e.target.value)}
                          sx={{ fontSize: 13 }}
                          disabled={r.qty_to_receive === 0}
                        >
                          <MenuItem value=""><em>(use header)</em></MenuItem>
                          {warehouses.map(w => (
                            <MenuItem key={w.code} value={w.code}>{w.code}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      )}
                    </TableCell>
                  </TableRow>
                  {isManufacturePo && r.qty_to_receive > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 1.5 }}>
                        {/* Finished-good batch metadata */}
                        <Grid container spacing={1.5} sx={{ mb: 1 }}>
                          <Grid item xs={4}>
                            <TextField fullWidth size="small" label="FG Batch # (manufacturer lot, optional)"
                              value={r.batch_number || ''}
                              onChange={(e) => updateReceiptLine(idx, 'batch_number', e.target.value)} />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField fullWidth size="small" type="date" label="Manufacturing date"
                              InputLabelProps={{ shrink: true }}
                              value={r.manufacturing_date || ''}
                              onChange={(e) => updateReceiptLine(idx, 'manufacturing_date', e.target.value)} />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField fullWidth size="small" type="date" label="Expiry date"
                              InputLabelProps={{ shrink: true }}
                              value={r.expiry_date || ''}
                              onChange={(e) => updateReceiptLine(idx, 'expiry_date', e.target.value)} />
                          </Grid>
                        </Grid>
                        {/* Component consumption grid (backflush actuals) */}
                        {(() => {
                          const bf = backflushByLine[r.po_item_id];
                          if (!bf || bf.loading) {
                            return <Typography variant="caption" color="text.secondary">Calculating component consumption…</Typography>;
                          }
                          if (bf.error) {
                            return <Alert severity="error" sx={{ py: 0 }}>{bf.error}</Alert>;
                          }
                          return (
                            <>
                              {bf.is_bulkfill && bf.produces_sku && (
                                <Alert severity="info" sx={{ py: 0, mb: 0.5 }}>
                                  Receiving {Number(r.qty_to_receive).toLocaleString()} × {bf.line_sku} builds{' '}
                                  <b>{Number(bf.produces_quantity || 0).toLocaleString()} × {bf.produces_sku}</b> — the
                                  finished good is what lands in stock at this warehouse.
                                </Alert>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  Components consumed (per BOM × {bf.is_bulkfill && bf.produces_quantity != null ? bf.produces_quantity : r.qty_to_receive} produced — edit for wastage)
                                </Typography>
                                {(bf.stale || bf.computed_for !== r.qty_to_receive) && (
                                  <Button size="small" variant="outlined" color="warning"
                                    onClick={() => loadBackflushPreview(r.po_item_id, r.qty_to_receive, receiptForm.receipt_date)}>
                                    Recalculate (qty changed)
                                  </Button>
                                )}
                              </Box>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Component</TableCell>
                                    <TableCell align="right">Per unit</TableCell>
                                    <TableCell align="right" sx={{ minWidth: 120 }}>Consume (actual)</TableCell>
                                    <TableCell align="right">Available</TableCell>
                                    <TableCell align="right">Est. cost</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {bf.components.map((c) => {
                                    const short = c.available_at_warehouse != null && Number(c.quantity) > Number(c.available_at_warehouse);
                                    return (
                                      <TableRow key={c.component_sku}>
                                        <TableCell>
                                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.component_sku}</Typography>
                                          <Typography variant="caption" color="text.secondary">{c.product_name}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          {c.bom_quantity}{c.bom_unit !== 'units' ? ` ${c.bom_unit}` : ''}
                                          {c.bom_unit !== c.stock_uom && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              = {c.required_per_unit_stock} {c.stock_uom}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="right">
                                          <TextField size="small" type="number"
                                            inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                                            value={c.quantity}
                                            onChange={(e) => setBackflushComponentQty(r.po_item_id, c.component_sku, e.target.value)} />
                                          {c.stock_uom !== 'units' && <Typography variant="caption" color="text.secondary"> {c.stock_uom}</Typography>}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: short ? 'error.main' : 'inherit', fontFamily: 'monospace' }}>
                                          {c.available_at_warehouse == null ? '—' : c.available_at_warehouse.toLocaleString()}
                                          {short && <Typography variant="caption" color="error" sx={{ display: 'block' }}>short</Typography>}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                          {c.estimated_cost != null ? Number(c.estimated_cost).toFixed(2) : '—'}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              {(bf.warnings || []).map((w, i) => (
                                <Typography key={i} variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>⚠ {w}</Typography>
                              ))}
                            </>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(() => {
            const totalRec = receiptForm.items.reduce((s, r) => s + Number(r.qty_to_receive || 0), 0);
            const totalAcc = receiptForm.items.reduce((s, r) => s + Number(r.qty_to_accept || 0), 0);
            const totalRej = totalRec - totalAcc;
            return (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 3, fontSize: 13 }}>
                <span>Receiving: <strong>{totalRec.toLocaleString()}</strong></span>
                <span>Accepting: <strong style={{ color: '#2e7d32' }}>{totalAcc.toLocaleString()}</strong></span>
                {totalRej > 0 && <span>Rejecting: <strong style={{ color: '#d32f2f' }}>{totalRej.toLocaleString()}</strong></span>}
              </Box>
            );
          })()}
          {!isManufacturePo && (
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch
                checked={!!receiptForm.skip_inventory}
                onChange={(e) => setReceiptForm({ ...receiptForm, skip_inventory: e.target.checked })}
              />}
              label="Stock already in inventory — record receipt without adding stock"
            />
            {receiptForm.skip_inventory && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                For goods already booked (e.g. via Cin7 transfers). Records the receipt and updates received qty, but adds NO new stock — avoids double-counting.
              </Typography>
            )}
          </Box>
          )}
          {isManufacturePo && Object.values(backflushByLine).some(bf =>
            (bf.components || []).some(c => c.available_at_warehouse != null && Number(c.quantity) > Number(c.available_at_warehouse))
          ) && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch checked={confirmShortfall} onChange={(e) => setConfirmShortfall(e.target.checked)} color="warning" />}
                label="Some components exceed available stock — consume anyway (stock will go negative until the next count)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
            Saves immediately — “Back” won’t undo a receipt; void it to reverse.
          </Typography>
          <Button onClick={() => setReceiptDialogOpen(false)} disabled={recordingReceipt}>Cancel</Button>
          <Button variant="contained" onClick={submitReceipt}
            disabled={recordingReceipt || !receiptForm.warehouse_code}
            title={!receiptForm.warehouse_code ? 'Pick a destination warehouse first' : ''}>
            {recordingReceipt ? 'Recording…' : 'Record Receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logistics & Customs (collapsible) */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={700}>Logistics & Customs</Typography>
          <Typography variant="caption" sx={{ ml: 1.5, color: 'text.disabled', alignSelf: 'center' }}>
            Tracking · Carrier · Incoterms · Ports · Dates
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Payment Terms" value={form.payment_terms} onChange={(e) => { setPaymentTermsManuallyEdited(true); setForm({ ...form, payment_terms: e.target.value }); }} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Payment Due Date" value={form.payment_due_date} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Tracking Code" value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Carrier" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Transport Mode</InputLabel>
                <Select value={form.transport_mode} label="Transport Mode"
                  onChange={(e) => setForm({ ...form, transport_mode: e.target.value })}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  <MenuItem value="Air">Air</MenuItem>
                  <MenuItem value="Sea">Sea</MenuItem>
                  <MenuItem value="Road">Road</MenuItem>
                  <MenuItem value="Rail">Rail</MenuItem>
                  <MenuItem value="Courier">Courier</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Bill of Lading" value={form.bill_of_lading} onChange={(e) => setForm({ ...form, bill_of_lading: e.target.value })} helperText={HELP.po.bill_of_lading} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Incoterms" placeholder="FOB / CIF / DDP / EXW" value={form.incoterms} onChange={(e) => setForm({ ...form, incoterms: e.target.value })} helperText={HELP.po.incoterms} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Manufacturing COO" placeholder="ISO-2 (e.g. CN, IN, IT)" value={form.manufacturing_coo} onChange={(e) => setForm({ ...form, manufacturing_coo: e.target.value })} helperText={HELP.po.manufacturing_coo} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Ex Factory Date" value={form.ex_factory_date} onChange={(e) => setForm({ ...form, ex_factory_date: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Port of Loading" value={form.port_of_loading} onChange={(e) => setForm({ ...form, port_of_loading: e.target.value })} helperText={HELP.po.port_of_loading} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Port of Discharge" value={form.port_of_discharge} onChange={(e) => setForm({ ...form, port_of_discharge: e.target.value })} helperText={HELP.po.port_of_discharge} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="ETA at Discharge" value={form.eta_port_discharge} onChange={(e) => setForm({ ...form, eta_port_discharge: e.target.value })} helperText={HELP.po.eta_port_discharge} /></Grid>
            {/* Landed-cost header inputs. Allocated across lines pro-rata by line value
                in the per-line "Landed Unit Cost" computed display. */}
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="number" label={`Freight Cost (${form.currency})`}
                inputProps={{ min: 0, step: 0.01 }}
                value={form.freight_cost}
                onChange={(e) => setForm({ ...form, freight_cost: parseFloat(e.target.value) || 0 })}
                helperText={HELP.po.freight_cost} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" type="number" label={`Customs Duty (${form.currency})`}
                inputProps={{ min: 0, step: 0.01 }}
                value={form.customs_duty}
                onChange={(e) => setForm({ ...form, customs_duty: parseFloat(e.target.value) || 0 })}
                helperText={HELP.po.customs_duty} />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Supplier workflow milestones (collapsible) */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={700}>Supplier Workflow Milestones</Typography>
          <Typography variant="caption" sx={{ ml: 1.5, color: 'text.disabled', alignSelf: 'center' }}>
            PO sent · accepted · port ETD · supplier invoice
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" label="Supplier PO Ref" placeholder="Their reference number" value={form.supplier_po_ref} onChange={(e) => setForm({ ...form, supplier_po_ref: e.target.value })} helperText={HELP.po.supplier_po_ref} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="PO Sent Date" value={form.po_sent_date} onChange={(e) => setForm({ ...form, po_sent_date: e.target.value })} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Supplier Acceptance Date" value={form.supplier_acceptance_date} onChange={(e) => setForm({ ...form, supplier_acceptance_date: e.target.value })} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Origin Port ETD" value={form.origin_port_etd} onChange={(e) => setForm({ ...form, origin_port_etd: e.target.value })} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Freight Booking Confirmed" value={form.freight_booking_confirmed_date} onChange={(e) => setForm({ ...form, freight_booking_confirmed_date: e.target.value })} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" label="Supplier Inv #" value={form.supplier_invoice_number} onChange={(e) => setForm({ ...form, supplier_invoice_number: e.target.value })} helperText={HELP.po.supplier_invoice_number} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Supplier Invoice Date" value={form.supplier_invoice_date}
              onChange={(e) => { const v = e.target.value; setForm({ ...form, supplier_invoice_date: v, payment_due_date: v ? poPaymentDueFromInvoice(v, form.payment_terms) : form.payment_due_date }); }}
              helperText="Sets the Payment Due Date from the terms" FormHelperTextProps={{ sx: { fontSize: 10 } }} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth size="small" type="number" label={`Supplier Invoice Total (${form.currency})`} inputProps={{ min: 0, step: 0.01 }} value={form.supplier_invoice_total} onChange={(e) => setForm({ ...form, supplier_invoice_total: parseFloat(e.target.value) || 0 })} /></Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Notes + Shipping address */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" multiline rows={2} label="Notes (visible to supplier)"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                helperText="Renders on supplier-facing PO docs" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" multiline rows={2} label="Internal Notes"
                value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                helperText="Procurement team only — never sent to supplier" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" multiline rows={2} label="Description of Goods"
                value={form.description_of_goods} onChange={(e) => setForm({ ...form, description_of_goods: e.target.value })}
                helperText="Customs / commercial-invoice text (required for cross-border)" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Terms & Conditions */}
      {(() => {
        const selSup = suppliers.find(s => Number(s.id) === Number(form.supplier_id));
        const hasSupTerms = !!(selSup && String(selSup.purchase_terms_html || '').trim());
        const hasPoOverride = !!String(form.terms_and_conditions || '').trim();
        let tcStatus;
        if (Number(form.suppress_terms) === 1) {
          tcStatus = { label: 'No T&Cs on this PO', color: 'default', detail: 'Suppress is on — PDF will render without any T&Cs appendix.' };
        } else if (hasPoOverride) {
          tcStatus = { label: 'Using PO-level override', color: 'info', detail: 'This PO\'s own T&Cs (below) win over supplier + subsidiary defaults.' };
        } else if (hasSupTerms) {
          tcStatus = { label: `Using ${selSup.supplier_name}'s default T&Cs`, color: 'success', detail: 'Negotiated supplier terms (managed on the Supplier screen) will attach to the PDF.' };
        } else {
          tcStatus = { label: 'Using the issuing subsidiary\'s default T&Cs', color: 'success', detail: 'Falls through to whatever T&Cs the subsidiary admin has authored (or none if not set).' };
        }
        // Collapse by default — operators rarely override per PO. Auto-expand
        // only when there's already an override or suppress is on (so the user
        // doesn't lose track of an active override after navigating away and
        // back).
        const hasActiveOverride = hasPoOverride || Number(form.suppress_terms) === 1;
        return (
          <Accordion sx={{ mt: 2 }} defaultExpanded={hasActiveOverride}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
                <Typography variant="subtitle1" fontWeight={700}>Terms &amp; Conditions</Typography>
                <Chip size="small" label={tcStatus.label} color={tcStatus.color} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Expand to override for this PO only
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {tcStatus.detail} <strong>Precedence:</strong> Suppress &gt; PO override &gt; Supplier default &gt; Subsidiary default.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" multiline rows={6}
                    label="PO-level T&C override (plain text — leave blank to use supplier / subsidiary defaults)"
                    placeholder="Optional. Type any clauses that should override the default T&Cs just for this PO."
                    value={form.terms_and_conditions}
                    onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })}
                    disabled={Number(form.suppress_terms) === 1}
                    helperText="Renders as the T&Cs appendix on this PO's PDF instead of supplier / subsidiary defaults. Plain text only."
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(form.suppress_terms) === 1}
                        onChange={(e) => setForm({ ...form, suppress_terms: e.target.checked ? 1 : 0 })}
                      />
                    }
                    label="Suppress all T&Cs on this PO's PDF"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })()}

      {/* Sticky action bar */}
      <Box sx={{
        position: 'fixed', bottom: 0, left: { xs: 0, md: 240 }, right: 0,
        bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
        px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        zIndex: 10, boxShadow: '0 -4px 16px -8px rgba(0,0,0,0.10)',
      }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {form.items.length} line{form.items.length === 1 ? '' : 's'} · {totals.qty} unit{totals.qty === 1 ? '' : 's'}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {formatCurrency(totals.total, form.currency)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* No footer "Cancel": it did exactly what the Back arrow does, and
              two controls for one action is the confusion Vishal flagged.
              Back is the single "leave" control and is guarded for unsaved
              edits (useUnsavedChangesGuard). */}
          {isEdit && (
            <Tooltip title={hasReceipts
              ? `Cannot delete — ${totalReceivedQty} unit(s) already received. Cancel the PO instead to keep the audit trail.`
              : 'Permanently delete this PO. Only allowed when no goods have been received.'}>
              <span>
                <Button color="error" variant="outlined" startIcon={<DeleteIcon />}
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={saving || deleting || hasReceipts}>
                  Delete
                </Button>
              </span>
            </Tooltip>
          )}
          {isEdit && (
            <Button variant="outlined" startIcon={<PrintIcon />}
              onClick={() => downloadPoPdf(editId, poNumber, setError)}
              disabled={saving || deleting}>
              Download PDF
            </Button>
          )}
          {/* ONE primary save ("Save & Close" — saves and returns to the list).
              The stay-on-page save is kept because it is a real workflow (add a
              receipt, save, record a payment, save) but is now clearly
              secondary AND self-describing: the old pair was "Save Changes" vs
              "Save & Close", which differ only in a behaviour you could not see.
              The labels now state what each one does, so no tooltip is needed. */}
          {isEdit ? (
            <>
              <Button variant="text" onClick={() => handleSave('stay')} disabled={saving || deleting || !!poClosedNotice}>
                {saving ? 'Saving…' : 'Save & keep editing'}
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave('back')} disabled={saving || deleting || !!poClosedNotice}>
                {saving ? 'Saving…' : 'Save & Close'}
              </Button>
            </>
          ) : (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave('back')} disabled={saving || deleting}>
              {saving ? 'Saving…' : 'Create PO'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Edit payment dialog */}
      <Dialog open={!!editingPayment} onClose={() => !savingPayment && setEditingPayment(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {editingPayment && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} label="Date"
                  value={editingPayment.payment_date}
                  onChange={(e) => setEditingPayment({ ...editingPayment, payment_date: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label={`Amount (${editingPayment.currency || form.currency})`}
                  inputProps={{ min: 0, step: 0.01 }}
                  value={editingPayment.amount}
                  onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })} />
              </Grid>
              {editingPayment.currency && editingPayment.currency !== form.currency && (
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label={`Applied to PO (${form.currency})`}
                    inputProps={{ min: 0, step: 0.01 }}
                    value={editingPayment.amount_in_po_currency}
                    onChange={(e) => setEditingPayment({ ...editingPayment, amount_in_po_currency: e.target.value })}
                    helperText="the amount this covers in the PO currency" />
                </Grid>
              )}
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Method</InputLabel>
                  <Select value={editingPayment.method} label="Method"
                    onChange={(e) => setEditingPayment({ ...editingPayment, method: e.target.value })}>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="wire">Wire</MenuItem>
                    <MenuItem value="ach">ACH</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Reference"
                  value={editingPayment.reference}
                  onChange={(e) => setEditingPayment({ ...editingPayment, reference: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Invoice #"
                  value={editingPayment.invoice_number || ''}
                  onChange={(e) => setEditingPayment({ ...editingPayment, invoice_number: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Notes"
                  value={editingPayment.notes}
                  onChange={(e) => setEditingPayment({ ...editingPayment, notes: e.target.value })} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingPayment(null)} disabled={savingPayment}>Cancel</Button>
          <Button variant="contained" onClick={saveEditPayment} disabled={savingPayment}>
            {savingPayment ? 'Saving…' : 'Save Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Purchase Order?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This permanently removes PO {poNumber || `#${editId}`} and its line items. Goods receipts, payments, and inventory history won't be affected (none exist for this PO).
          </Typography>
          <Typography variant="caption" color="text.secondary">
            If you want to keep the audit trail, click Cancel below and use the PO's <strong>Cancel</strong> action instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unreceiveConfirmOpen} onClose={() => !unreceiving && setUnreceiveConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Un-receive this PO?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This clears the phantom <strong>{totalReceivedQty.toLocaleString()}</strong> received on PO {poNumber || `#${editId}`} and reopens it. It changes <strong>no</strong> inventory or cost — there is no receipt or stock behind this number.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            If this PO actually has received stock this will be blocked — use <strong>Record Receipt</strong> / the receipt's reject action instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnreceiveConfirmOpen(false)} disabled={unreceiving}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleUnreceivePhantom} disabled={unreceiving}>
            {unreceiving ? 'Un-receiving…' : 'Un-receive'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
